import React, { useState, useMemo, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { format } from "date-fns";
import {
  FormControl,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  TextField,
  Autocomplete,
  Popper,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ExpandMore, DeleteOutline } from "@mui/icons-material";
import { IoIosArrowBack } from "react-icons/io";
import { CustomNoRowsOverlay } from "../../../components/common/OverlayGrid";
import {
  useFetchInvoiceProjectsQuery,
  useFetchInvoicesQueueQuery,
  useParseInvoiceResourcesMutation,
  useAllocateInvoiceMutation,
  useAllocateContractInvoiceMutation,
  useLazyFetchInvoiceByIdQuery,
  useFetchProjectResourcesMutation,
  useFetchPreviousInvoicesMutation,
  useFixedCostMapInvoiceMutation,
  useFetchGetallUserListQuery,
  useFetchGetAllProjectBasedUserNameListQuery,
  useFetchRatePerHourQuery,
} from "../../../slice/logdetailSlice";
import { getBudgetTypeInfo, buildExistingProjectsMap } from "../../../utils/budgetTypeUtils";
import { useTheme } from "../../../ThemeContext";
import ProjectSelectAutocomplete from "../../../components/common/ProjectSelectAutocomplete";

/**
 * Converts working hours (e.g. "4hrs 37min", "4.37", "04:37", 4) to decimal hours.
 * Formula: hours + (minutes / 60)
 * Example: 4hrs 37min -> 37 / 60 = 0.616666... + 4 = 4.616666666666667
 */
export const parseWorkingHoursToDecimal = (hoursVal) => {
  if (hoursVal === null || hoursVal === undefined || hoursVal === "") return 0;
  const str = String(hoursVal).trim();
  if (!str || str === "0" || str === "00.00" || str === "0.0") return 0;

  // 1. Text format like "4hrs 37min", "4 hrs 25 mins", "4h 37m", "37 mins"
  if (/hrs?|mins?|h\b|m\b/i.test(str)) {
    const hrMatch = str.match(/(\d+(?:\.\d+)?)\s*(?:hrs?|h)\b/i);
    const minMatch = str.match(/(\d+(?:\.\d+)?)\s*(?:mins?|m)\b/i);
    const h = hrMatch ? parseFloat(hrMatch[1]) || 0 : 0;
    const m = minMatch ? parseFloat(minMatch[1]) || 0 : 0;
    return h + (m / 60);
  }

  // 2. Colon separator like "04:37"
  if (str.includes(":")) {
    const parts = str.split(":");
    const h = parseFloat(parts[0]) || 0;
    const m = parseFloat(parts[1]) || 0;
    return h + (m / 60);
  }

  // 3. Dot separator like "04.37" (HH.MM)
  if (str.includes(".")) {
    const parts = str.split(".");
    const h = parseFloat(parts[0]) || 0;
    const m = parseFloat(parts[1]) || 0;
    return h + (m / 60);
  }

  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

/**
 * Isolated Billed input component to prevent DataGrid from stealing keyboard
 * events (Left/Right arrows, Home, End, Backspace) and preserve cursor position.
 */
const BilledCellInput = React.memo(({ idx, value, onChange, isDarkMode }) => {
  const [localVal, setLocalVal] = useState(
    value !== undefined && value !== null ? String(value) : ""
  );
  const inputRef = useRef(null);
  const cursorRef = useRef(null);
  const isFocusedRef = useRef(false);

  // Sync from props only when value changes externally (not while the user is actively typing)
  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalVal(value !== undefined && value !== null ? String(value) : "");
    }
  }, [value]);

  // Restore cursor position synchronously right after React reconciles DOM
  useLayoutEffect(() => {
    if (isFocusedRef.current && cursorRef.current !== null && inputRef.current) {
      const pos = cursorRef.current;
      try {
        inputRef.current.setSelectionRange(pos, pos);
      } catch (e) {
        // ignore if not supported
      }
    }
  });

  // Attach native capture & bubble keydown listeners directly to DOM input element
  // so MUI DataGrid's internal keyboard navigation can NEVER intercept Left/Right arrows, Home, End, or Backspace.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;

    const stopDataGridNavigation = (e) => {
      if (e.key === "Tab") {
        return; // Allow Tab to navigate to next input naturally
      }
      // Stop native propagation so DataGrid's root/cell listeners never see arrow keys
      e.stopPropagation();
      if (e.stopImmediatePropagation) {
        e.stopImmediatePropagation();
      }
    };

    el.addEventListener("keydown", stopDataGridNavigation, true); // Capture phase
    el.addEventListener("keydown", stopDataGridNavigation, false); // Bubble phase

    return () => {
      el.removeEventListener("keydown", stopDataGridNavigation, true);
      el.removeEventListener("keydown", stopDataGridNavigation, false);
    };
  }, []);

  const handleChange = (e) => {
    const raw = e.target.value;
    const cleanVal = raw.replace(/[^0-9.]/g, "");
    const parts = cleanVal.split(".");
    const sanitized = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : cleanVal;

    // Preserve cursor position relative to any deleted or inserted characters
    cursorRef.current = e.target.selectionStart;

    setLocalVal(sanitized);
    onChange(idx, sanitized);
  };

  const handleSelect = (e) => {
    cursorRef.current = e.target.selectionStart;
  };

  const handleFocus = (e) => {
    isFocusedRef.current = true;
    cursorRef.current = e.target.selectionStart;
    e.stopPropagation();
  };

  const handleBlur = (e) => {
    isFocusedRef.current = false;
    cursorRef.current = null;
  };

  return (
    <TextField
      size="small"
      value={localVal}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClick={(e) => {
        e.stopPropagation();
        cursorRef.current = e.target.selectionStart;
      }}
      onMouseDown={(e) => e.stopPropagation()}
      placeholder="0.00"
      inputRef={inputRef}
      InputProps={{
        startAdornment: (
          <span
            style={{
              fontSize: "12px",
              color: isDarkMode ? "#94a3b8" : "#64748b",
              marginRight: "3px",
              userSelect: "none",
            }}
          >
            ₹
          </span>
        ),
      }}
      inputProps={{
        onSelect: handleSelect,
        onKeyDown: (e) => e.stopPropagation(),
        onKeyDownCapture: (e) => e.stopPropagation(),
        style: {
          padding: "4px 8px 4px 0",
          textAlign: "right",
          fontSize: "12.5px",
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          color: isDarkMode ? "#f1f5f9" : "#1e293b",
        },
      }}
      sx={{
        width: 125,
        "& .MuiOutlinedInput-root": {
          height: "32px",
          backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
          borderRadius: "6px",
          "& fieldset": {
            borderColor: isDarkMode ? "#444" : "#cbd5e1",
          },
          "&:hover fieldset": {
            borderColor: isDarkMode ? "#666" : "#94a3b8",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#04a189",
            borderWidth: "1.5px",
          },
        },
      }}
    />
  );
});

/**
 * Screen 2: Contract Mapping & Auto-Allocation Modal
 * Built to specifications from Mockup Screen 2.
 * Handles retainer/T&M contract line parsing, auto-matching, and instant allocation.
 */
const ContractMappingModal = ({
  show,
  onHide,
  invoice,
  existingInvoices = [],
  onConfirmAllocate,
  onSaveFixedMapping,
  onOpenReviewMatches,
  onOpenFinalSplit,
  onSwitchToFixed,
  isInline = false,
}) => {
  const { isDarkMode } = useTheme();
  const { data: projectsResponse } = useFetchInvoiceProjectsQuery();
  const projectList = useMemo(() => projectsResponse?.data || [], [projectsResponse]);

  // Fetch fresh invoices queue to populate project status badges (Allocated, Mapped, etc.)
  const { data: queueResponse } = useFetchInvoicesQueueQuery({}, { skip: !show && !isInline });

  // Map of project IDs and project names that already have an invoice record in queue
  const existingProjectsMap = useMemo(() => {
    return buildExistingProjectsMap(
      existingInvoices,
      queueResponse?.data,
      projectList
    );
  }, [existingInvoices, queueResponse, projectList]);

  // All projects list (Contract, T&M, Fixed Cost)
  const allProjects = useMemo(() => {
    if (!Array.isArray(projectList)) return [];
    return projectList;
  }, [projectList]);

  const currentInvoiceId = Number(invoice?.rawItem?.invoice_id || invoice?.id || invoice?.invoice_id || 0);

  const handleClose = () => {
    if (currentInvoiceId) {
      sessionStorage.removeItem(`re_draft_proj_${currentInvoiceId}`);
      sessionStorage.removeItem(`re_draft_model_${currentInvoiceId}`);
      sessionStorage.removeItem(`re_draft_lines_${currentInvoiceId}`);
      sessionStorage.removeItem(`re_draft_users_${currentInvoiceId}`);
    }
    onHide?.();
  };

  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    if (currentInvoiceId) {
      try {
        const saved = sessionStorage.getItem(`re_draft_proj_${currentInvoiceId}`);
        if (saved && saved !== "—" && saved !== "-") return saved;
      } catch (e) {}
    }
    const incoming = invoice?.rawItem?.project_id || invoice?.project_id;
    return (incoming && incoming !== "—" && incoming !== "-") ? String(incoming) : "";
  });
  const [activeModel, setActiveModel] = useState(() => {
    if (currentInvoiceId) {
      try {
        const saved = sessionStorage.getItem(`re_draft_model_${currentInvoiceId}`);
        if (saved) return saved;
      } catch (e) {}
    }
    const bType = (invoice?.rawItem?.budget_type || invoice?.budget_type || "").toUpperCase();
    if (invoice?.model === "Fixed" || bType === "FIXED" || bType === "FIXED_COST") return "Fixed";
    if (
      invoice?.model === "Contract" ||
      bType === "CONTRACT" ||
      bType === "TIME_AND_MATERIAL" ||
      bType === "TIME AND MATERIAL" ||
      bType === "T&M"
    ) return "Contract";
    return invoice?.model || "";
  });
  const [mapScope, setMapScope] = useState("Base scope");
  const [lines, setLines] = useState(() => {
    if (currentInvoiceId) {
      try {
        const saved = sessionStorage.getItem(`re_draft_lines_${currentInvoiceId}`);
        if (saved) return JSON.parse(saved);
      } catch (e) {}
    }
    return [];
  });
  const [projectUsers, setProjectUsers] = useState(() => {
    if (currentInvoiceId) {
      try {
        const saved = sessionStorage.getItem(`re_draft_users_${currentInvoiceId}`);
        if (saved) return JSON.parse(saved);
      } catch (e) {}
    }
    return [];
  });

  const [parseInvoiceResources, { isLoading: isParsing }] =
    useParseInvoiceResourcesMutation();
  const [allocateInvoice, { isLoading: isAllocating }] =
    useAllocateInvoiceMutation();
  const [allocateContractInvoice, { isLoading: isAllocatingContract }] =
    useAllocateContractInvoiceMutation();
  const [fetchProjectResources, { isLoading: isFetchingProjectResources }] =
    useFetchProjectResourcesMutation();
  const [fetchInvoiceById, { data: fetchedInvoiceResponse, isLoading: isFetchingInvoice, isFetching }] =
    useLazyFetchInvoiceByIdQuery();

  const [prevInvoices, setPrevInvoices] = useState([]);
  const [prevInvoicesSummary, setPrevInvoicesSummary] = useState(null);
  const [fetchPreviousInvoices, { isLoading: isLoadingPrevInvoices }] =
    useFetchPreviousInvoicesMutation();
  const [fixedCostMapInvoice, { isLoading: isMappingFixed }] =
    useFixedCostMapInvoiceMutation();

  const [fetchedInvoice, setFetchedInvoice] = useState(null);
  const [loadedInvoiceId, setLoadedInvoiceId] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(() => {
    // If we already have draft lines restored, we don't need a blocking initial loader
    if (currentInvoiceId) {
      const savedLines = sessionStorage.getItem(`re_draft_lines_${currentInvoiceId}`);
      if (savedLines) return false;
    }
    return true;
  });
  const invoiceDetail = fetchedInvoice || fetchedInvoiceResponse?.data || invoice;

  const rawSource = (
    fetchedInvoice?.source ||
    invoiceDetail?.source ||
    invoice?.source ||
    invoice?.rawItem?.source ||
    ""
  ).toUpperCase();
  const invoiceNumberStr = String(
    fetchedInvoice?.invoice_number ||
    invoiceDetail?.invoice_number ||
    invoice?.invoice_number ||
    invoice?.invoice ||
    ""
  ).toUpperCase();
  const isManualInvoice =
    rawSource === "MANUAL" ||
    rawSource === "MANINV" ||
    rawSource === "CASH" ||
    invoiceNumberStr.startsWith("MAN");

  const isInvoiceReady = (loadedInvoiceId === currentInvoiceId && currentInvoiceId !== 0) || (lines.length > 0 && selectedProjectId);
  const isLoadingResources = Boolean(
    (isParsing || isFetchingProjectResources || isFetchingInvoice || isFetching || isLoadingDetails) && lines.length === 0
  );


  const lastInvoiceIdRef = useRef(currentInvoiceId || null);
  const lastFetchedParamsRef = useRef("");
  const lastInvoiceFetchIdRef = useRef(null);
  const lastPrevInvoicesFetchKeyRef = useRef("");

  useEffect(() => {
    if (!show && !isInline) return;
    const invId = Number(invoice?.rawItem?.invoice_id || invoice?.id || invoice?.invoice_id || 0);
    // Only reset state if actually switching to a DIFFERENT invoice
    if (lastInvoiceIdRef.current && invId && lastInvoiceIdRef.current !== invId) {
      setFetchedInvoice(null);
      setLoadedInvoiceId(null);
      setIsLoadingDetails(true);
      setLines([]);
      setProjectUsers([]);
      setSelectedProjectId("");
      setActiveModel("");
      lastFetchedParamsRef.current = "";
      lastInvoiceFetchIdRef.current = null;
      lastPrevInvoicesFetchKeyRef.current = "";
    }
    lastInvoiceIdRef.current = invId;
  }, [show, isInline, invoice?.id, invoice?.rawItem?.invoice_id]);

  const isAllocated =
    invoiceDetail?.status?.toLowerCase() === "allocated" ||
    invoice?.status?.toLowerCase() === "allocated" ||
    invoice?.rawItem?.status?.toLowerCase() === "allocated";
  const isMapped =
    invoiceDetail?.status?.toLowerCase() === "mapped" ||
    invoice?.status?.toLowerCase() === "mapped" ||
    invoice?.rawItem?.status?.toLowerCase() === "mapped";
  const isModelLocked = Boolean(
    isAllocated ||
    (isMapped && ((invoiceDetail?.budget_type || invoice?.budget_type) === "FIXED" || invoice?.model === "Fixed"))
  );

  const cleanProjId = (id) =>
    id && id !== "—" && id !== "-" && id !== "null" && id !== "undefined" ? String(id).trim() : "";

  const activeProjectId =
    cleanProjId(selectedProjectId) ||
    cleanProjId(invoiceDetail?.project_id) ||
    cleanProjId(invoiceDetail?.project?.project_id) ||
    cleanProjId(invoice?.rawItem?.project_id) ||
    cleanProjId(invoice?.project_id) ||
    (invoice?.project && invoice?.project !== "—" && invoice?.project !== "-" ? invoice.project : "");

  const hasProjectSelected = Boolean(activeProjectId);

  const isProjectLocked = Boolean(
    isAllocated ||
    isManualInvoice ||
    (hasProjectSelected && (isMapped || isAllocated))
  );

  const matchedProjectObj = allProjects.find(
    (p) =>
      (activeProjectId && (String(p.project_id) === String(activeProjectId) || String(p.id) === String(activeProjectId))) ||
      (invoiceDetail?.project?.title && p.title === invoiceDetail.project.title) ||
      (invoice?.project && invoice.project !== "—" && invoice.project !== "-" && (p.title === invoice.project || p.project_name === invoice.project)) ||
      (activeProjectId && p.title === activeProjectId)
  );

  const targetProjId = matchedProjectObj?.project_id || matchedProjectObj?.id || activeProjectId;

  const { data: projectTeamData } = useFetchGetAllProjectBasedUserNameListQuery(
    targetProjId,
    { skip: !targetProjId }
  );
  const { data: allUsersResponse } = useFetchGetallUserListQuery();
  const { data: ratePerHourResponse } = useFetchRatePerHourQuery();


  const rawType =
    invoiceDetail?.project?.budget_type ||
    invoiceDetail?.budget_type ||
    matchedProjectObj?.budget_type ||
    invoice?.rawItem?.budget_type ||
    invoice?.budget_type ||
    "";

  const selectedBudgetInfo = useMemo(() => {
    return getBudgetTypeInfo(rawType);
  }, [rawType]);

  // Reset or initialize state whenever modal is opened or invoice changes
  useEffect(() => {
    if ((show || isInline) && invoice) {
      const invId = invoice?.rawItem?.invoice_id || invoice?.id || invoice?.invoice_id;
      const savedDraftProj = invId ? sessionStorage.getItem(`re_draft_proj_${invId}`) : "";
      const savedDraftModel = invId ? sessionStorage.getItem(`re_draft_model_${invId}`) : "";

      setMapScope("Base scope");

      const incomingRaw = invoice?.rawItem?.project_id || invoice?.project_id;
      const incomingProjId =
        incomingRaw && incomingRaw !== "—" && incomingRaw !== "-"
          ? incomingRaw
          : (savedDraftProj && savedDraftProj !== "—" && savedDraftProj !== "-" ? savedDraftProj : "");

      const matchedProj = allProjects.find(
        (p) =>
          (incomingProjId && (String(p.project_id) === String(incomingProjId) || String(p.id) === String(incomingProjId))) ||
          (invoice?.project && invoice.project !== "—" && invoice.project !== "-" && (p.title === invoice.project || p.project_name === invoice.project)) ||
          (savedDraftProj && savedDraftProj !== "—" && savedDraftProj !== "-" && (p.title === savedDraftProj || p.project_name === savedDraftProj))
      );
      const existingProjId =
        incomingProjId ||
        matchedProj?.project_id ||
        matchedProj?.id ||
        "";

      setSelectedProjectId(
        existingProjId && existingProjId !== "—" && existingProjId !== "-" ? String(existingProjId) : ""
      );

      const bType = matchedProj?.budget_type || invoice?.budget_type || "";
      const budgetInfo = getBudgetTypeInfo(bType);

      if (savedDraftModel) {
        setActiveModel(savedDraftModel);
      } else if (invoice?.model === "Fixed" || budgetInfo.model === "Fixed") {
        setActiveModel("Fixed");
      } else if (invoice?.model === "Contract" || budgetInfo.model === "Contract") {
        setActiveModel("Contract");
      } else {
        setActiveModel(invoice?.model || "");
      }

    } else if (!show && !isInline) {
      setSelectedProjectId("");
      setLines([]);
      setProjectUsers([]);
      setActiveModel("");
      setMapScope("Base scope");
      setIsLoadingDetails(true);
      setLoadedInvoiceId(null);
    }
  }, [show, isInline, invoice?.id, invoice?.rawItem?.invoice_id, allProjects]);

  // Fetch previous invoices when Fixed Cost mode is active and a project is selected
  useEffect(() => {
    if (!show && !isInline) return;

    const projId =
      matchedProjectObj?.project_id ||
      (Number(activeProjectId) ? Number(activeProjectId) : null) ||
      (Number(selectedProjectId) ? Number(selectedProjectId) : null);
    const isFixed =
      activeModel === "Fixed" ||
      String(rawType).toUpperCase().includes("FIXED") ||
      invoice?.model === "Fixed";

    if (projId && isFixed) {
      const fetchKey = `prev_${Number(projId)}`;
      if (lastPrevInvoicesFetchKeyRef.current === fetchKey) {
        return;
      }
      lastPrevInvoicesFetchKeyRef.current = fetchKey;

      fetchPreviousInvoices({ project_id: Number(projId) })
        .unwrap()
        .then((res) => {
          if (res?.data) {
            setPrevInvoices(res.data);
            setPrevInvoicesSummary(res.summary || null);
          } else if (Array.isArray(res)) {
            setPrevInvoices(res);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch previous invoices:", err);
          lastPrevInvoicesFetchKeyRef.current = "";
        });
    } else if (!activeProjectId && !selectedProjectId) {
      lastPrevInvoicesFetchKeyRef.current = "";
      setPrevInvoices([]);
      setPrevInvoicesSummary(null);
    }
  }, [
    show,
    isInline,
    activeProjectId,
    selectedProjectId,
    matchedProjectObj?.project_id,
    activeModel,
    rawType,
    invoice?.model,
    fetchPreviousInvoices,
  ]);

  const handleProjectSelect = async (newProjId) => {
    if (!newProjId) {
      setSelectedProjectId("");
      setLines([]);
      setProjectUsers([]);
      setLoadedInvoiceId(null);
      setIsLoadingDetails(false);
      lastFetchedParamsRef.current = "";
      lastPrevInvoicesFetchKeyRef.current = "";
      setActiveModel("");
      const invId = invoice?.rawItem?.invoice_id || invoice?.id || invoice?.invoice_id;
      if (invId) {
        sessionStorage.removeItem(`re_draft_proj_${invId}`);
        sessionStorage.removeItem(`re_draft_model_${invId}`);
        sessionStorage.removeItem(`re_draft_lines_${invId}`);
        sessionStorage.removeItem(`re_draft_users_${invId}`);
      }
      return;
    }

    const chosen = allProjects.find(
      (p) =>
        String(p.project_id) === String(newProjId) ||
        String(p.id) === String(newProjId) ||
        p.title === newProjId ||
        p.project_name === newProjId
    );

    // Client mismatch check
    const currentInvoiceClient = (
      invoice?.client ||
      invoice?.client_name ||
      invoice?.rawItem?.client_name ||
      invoiceDetail?.client_name ||
      ""
    ).trim();

    const existingMappedInv = (existingInvoices || [])
      .concat(queueResponse?.data || [])
      .find((inv) => {
        const pId = inv.project_id || inv.rawItem?.project_id || inv.project?.project_id;
        const invClient = (inv.client_name || inv.client || inv.rawItem?.client_name || "").trim();
        return (
          String(pId) === String(newProjId) &&
          invClient &&
          invClient !== "—" &&
          invClient !== "-" &&
          invClient.toLowerCase() !== String(inv.project || inv.project_name || "").toLowerCase()
        );
      });

    const projectExistingClient = (
      chosen?.client_name ||
      existingMappedInv?.client_name ||
      existingMappedInv?.client ||
      existingMappedInv?.rawItem?.client_name ||
      ""
    ).trim();

    if (
      currentInvoiceClient &&
      currentInvoiceClient !== "—" &&
      currentInvoiceClient !== "-" &&
      projectExistingClient &&
      projectExistingClient !== "—" &&
      projectExistingClient !== "-" &&
      currentInvoiceClient.toLowerCase() !== projectExistingClient.toLowerCase()
    ) {
      const projTitle = chosen?.title || chosen?.project_name || chosen?.name || `Project #${newProjId}`;
      const invNum = invoice?.invoice || invoice?.invoice_number || invoice?.rawItem?.invoice_number || "";
      const invLabel = invNum ? `invoice <strong>"${invNum}"</strong> for ` : "";

      const result = await Swal.fire({
        title: "Client Mismatch Warning",
        html: `The selected project <strong>"${projTitle}"</strong> is already mapped to client <strong>"${projectExistingClient}"</strong>.<br/><br/>Do you want to map this project to ${invLabel}client <strong>"${currentInvoiceClient}"</strong>?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#10b981",
        cancelButtonColor: "#ef4444",
        confirmButtonText: "Yes, Map Project",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    setSelectedProjectId(newProjId);
    setLines([]);
    setProjectUsers([]);
    setLoadedInvoiceId(null);
    setIsLoadingDetails(true);
    lastFetchedParamsRef.current = "";
    lastPrevInvoicesFetchKeyRef.current = "";

    const invId = invoice?.rawItem?.invoice_id || invoice?.id || invoice?.invoice_id;

    let model = "";
    if (chosen) {
      const budgetInfo = getBudgetTypeInfo(chosen.budget_type);
      model = budgetInfo.model || "";
    }
    setActiveModel(model);

    if (invId) {
      sessionStorage.setItem(`re_draft_proj_${invId}`, String(newProjId));
      if (model) sessionStorage.setItem(`re_draft_model_${invId}`, model);
    }
  };

  const handleSaveFixed = async () => {
    if (!selectedProjectId) {
      toast.warning("Please select a project before saving.");
      return;
    }
    const matched = allProjects.find(
      (p) =>
        String(p.project_id) === String(selectedProjectId) ||
        String(p.id) === String(selectedProjectId) ||
        p.title === selectedProjectId ||
        p.project_name === selectedProjectId
    );
    const chosenProjId = matched?.project_id || matched?.id || selectedProjectId;
    const chosenProjectTitle =
      matched?.title || matched?.project_name || selectedProjectId || invoice?.project;

    if (!selectedBudgetInfo.isAssigned) {
      toast.error(
        `Project "${chosenProjectTitle}" does not have a Budget Type assigned in Project Details. Please configure Budget Type before mapping.`
      );
      return;
    }

    const invId =
      invoiceDetail?.invoice_id ||
      invoiceDetail?.id ||
      invoice?.rawItem?.invoice_id ||
      invoice?.id ||
      invoice?.invoice_id;

    if (!invId) {
      toast.error("Invoice ID is missing.");
      return;
    }

    // Manual invoices are already assigned to project_id during creation via /manual-invoice/create
    if (isManualInvoice) {
      toast.success(
        `Manual invoice ${invoice?.invoice || ""} is mapped to ${chosenProjectTitle} successfully!`
      );
      if (onSaveFixedMapping) {
        onSaveFixedMapping({
          project: chosenProjectTitle,
          project_id: chosenProjId,
          scope: mapScope,
        });
      }
      handleClose();
      return;
    }

    try {
      const payload = {
        invoice_id: Number(invId),
        project_id: Number(chosenProjId),
      };
      console.log("Mapping fixed cost invoice with payload:", payload);
      const res = await fixedCostMapInvoice(payload).unwrap();
      toast.success(res?.message || `Invoice ${invoice?.invoice || ""} mapped to ${chosenProjectTitle} successfully!`);
      if (onSaveFixedMapping) {
        onSaveFixedMapping({
          project: chosenProjectTitle,
          project_id: chosenProjId,
          scope: mapScope,
          data: res?.data,
        });
      }
      handleClose();
    } catch (err) {
      console.error("Fixed cost mapping error:", err);
      toast.error(err?.data?.message || err?.message || "Failed to map invoice");
    }
  };



  const getValidReportingMonth = (customRepMonth) => {
    const raw =
      customRepMonth ||
      invoiceDetail?.reporting_month ||
      invoice?.rawItem?.reporting_month ||
      invoice?.reporting_month;
    if (raw && typeof raw === "string") {
      const trimmed = raw.trim();
      if (/^\d{4}-\d{2}$/.test(trimmed)) {
        return trimmed;
      }
      try {
        const d = new Date(trimmed.includes(" ") ? `01 ${trimmed}` : trimmed);
        if (!isNaN(d.getTime())) {
          return format(d, "yyyy-MM");
        }
      } catch { }
    }
    const rawDate =
      invoiceDetail?.invoiced_date ||
      invoiceDetail?.payment_date ||
      invoice?.rawItem?.invoiced_date ||
      invoice?.rawItem?.payment_date ||
      invoice?.rawItem?.created_at ||
      invoice?.date;
    if (rawDate) {
      try {
        const d = new Date(rawDate);
        if (!isNaN(d.getTime())) {
          return format(d, "yyyy-MM");
        }
      } catch { }
    }
    return format(new Date(), "yyyy-MM");
  };

  // Fetch parsed resources whenever invoice or selected project changes
  useEffect(() => {
    if ((show || isInline) && invoice) {
      const invId = invoice?.rawItem?.invoice_id || invoice?.id || invoice?.invoice_id;
      const projId = selectedProjectId || invoice?.rawItem?.project_id || invoice?.project_id;
      const repMonth = getValidReportingMonth();

      if (!invId) return;

      const doFetchResources = (invoiceData) => {
        const targetProjId = selectedProjectId || invoiceData?.project_id || invoiceData?.project?.project_id || projId;
        const bType = (invoiceData?.budget_type || invoiceData?.project?.budget_type || invoice?.budget_type || "").toUpperCase();
        const isContractModel =
          activeModel === "Contract" ||
          bType === "CONTRACT" ||
          bType === "TIME_AND_MATERIAL" ||
          bType === "TIME AND MATERIAL" ||
          bType === "T&M" ||
          invoice?.model === "Contract";

        if (targetProjId && isContractModel) {
          if (isManualInvoice) {
            const receivedAmount =
              invoiceData?.total_amount ||
              invoice?.rawItem?.total_amount ||
              invoice?.total_amount ||
              invoice?.amount ||
              0;
            const scopeId =
              invoiceData?.scope_id ||
              invoice?.rawItem?.scope_id ||
              invoice?.scope_id ||
              null;

            const fetchKey = `manual_${invId}_${targetProjId}_${repMonth}_${receivedAmount}_${scopeId || ""}`;
            if (lastFetchedParamsRef.current === fetchKey) {
              setIsLoadingDetails(false);
              return;
            }
            lastFetchedParamsRef.current = fetchKey;

            setIsLoadingDetails(true);
            fetchProjectResources({
              project_id: Number(targetProjId),
              reporting_month: repMonth,
              received_amount: String(parseFloat(receivedAmount) || receivedAmount),
              ...(scopeId ? { scope_id: scopeId } : {}),
            })
              .unwrap()
              .then((res) => {
                if (res && res.status && Array.isArray(res.data)) {
                  const rawItems = res.data;
                  const usersList = rawItems.map((item) => ({
                    user_id: Number(item.user_id),
                    name: item.name,
                    rate_per_hour: Number(item.rate_per_hour || 0),
                    working_hours: item.working_hours,
                  }));
                  setProjectUsers(usersList);

                  const mappedLines = rawItems.map((item, idx) => {
                    const earningVal = Number(item.earning !== undefined ? item.earning : (item.billed_amount || 0));
                    const costVal = Number(item.cost || 0);
                    const marginVal = Number(item.profit_margin !== undefined ? item.profit_margin : (earningVal - costVal));
                    const rawHrs = item.working_hours ?? 0;
                    const decimalHrs = parseWorkingHoursToDecimal(rawHrs);

                    return {
                      id: `resource-${item.user_id || idx}`,
                      line_item_id: String(item.user_id || idx + 1),
                      line_item_name: item.name,
                      user_id: item.user_id ? Number(item.user_id) : null,
                      resource_name: item.name,
                      name: item.name,
                      billing_type: "Billable",
                      working_hours: item.working_hours ?? decimalHrs,
                      raw_working_hours: rawHrs,
                      extracted_hours: decimalHrs,
                      rate_per_hour: Number(item.rate_per_hour || 0),
                      earning: earningVal,
                      billed_amount: earningVal,
                      cost: costVal,
                      profit_margin: marginVal,
                      computed_share: item.computed_share,
                      adjusted_share: item.adjusted_share,
                      is_auto: true,
                    };
                  });

                  // Restore draft custom line edits if present
                  const savedDraftLinesStr = invId ? sessionStorage.getItem(`re_draft_lines_${invId}`) : null;
                  let finalLines = mappedLines;
                  if (savedDraftLinesStr) {
                    try {
                      const savedDraft = JSON.parse(savedDraftLinesStr);
                      if (Array.isArray(savedDraft) && savedDraft.length === mappedLines.length) {
                        finalLines = savedDraft;
                      }
                    } catch (e) {}
                  }
                  setLines(finalLines);
                } else {
                  setLines([]);
                  setProjectUsers([]);
                }
                setLoadedInvoiceId(Number(invId));
              })
              .catch((err) => {
                console.error("Failed to fetch project resources:", err);
                setLines([]);
                setProjectUsers([]);
                setLoadedInvoiceId(Number(invId));
                lastFetchedParamsRef.current = "";
              })
              .finally(() => {
                setIsLoadingDetails(false);
              });
          } else {
            // Synced Zoho invoice: run parseInvoiceResources
            const parseKey = `zoho_${invId}_${targetProjId}_${repMonth}`;
            if (lastFetchedParamsRef.current === parseKey) {
              setIsLoadingDetails(false);
              return;
            }
            lastFetchedParamsRef.current = parseKey;

            setIsLoadingDetails(true);
            parseInvoiceResources({
              invoice_id: Number(invId),
              project_id: Number(targetProjId),
              reporting_month: repMonth,
            })
              .unwrap()
              .then((res) => {
                if (res && res.status) {
                  let rawItems = res.parsed_items || res.parsed_resources || res.data;
                  if (!rawItems && Array.isArray(res.project_users)) {
                    const lineItemsInUsers = res.project_users.filter((x) => x.line_item_id);
                    if (lineItemsInUsers.length > 0) {
                      rawItems = lineItemsInUsers;
                    }
                  }
                  const cleanUsers = Array.isArray(res.project_users)
                    ? res.project_users.filter((u) => u.user_id && !u.line_item_id)
                    : [];
                  const usersList = cleanUsers.length > 0 ? cleanUsers : (res.project_users || []);
                  setProjectUsers(usersList);

                  const validItems = Array.isArray(rawItems) ? rawItems : [];
                  const mappedLines = validItems.map((item) => {
                    const billedVal = item.earning !== undefined ? Number(item.earning) : Number(item.billed_amount || 0);
                    const matchedUser = usersList.find((u) => Number(u.user_id) === Number(item.user_id));
                    const isReimb = item.billing_type?.toLowerCase() === "reimbursement";

                    const rawHrs = matchedUser && matchedUser.working_hours !== undefined && matchedUser.working_hours !== null
                      ? matchedUser.working_hours
                      : (item.working_hours ?? item.extracted_hours ?? 0);
                    const decimalHrs = parseWorkingHoursToDecimal(rawHrs);

                    let costVal = Number(item.cost || 0);
                    if (!isReimb && matchedUser) {
                      const rate = Number(matchedUser.rate_per_hour || 0);
                      costVal = Math.round((decimalHrs * rate + Number.EPSILON) * 100) / 100;
                    }

                    const marginVal = isReimb && item.profit_margin !== undefined
                      ? Number(item.profit_margin)
                      : Math.round((billedVal - costVal + Number.EPSILON) * 100) / 100;

                    return {
                      ...item,
                      user_id: item.user_id ? Number(item.user_id) : null,
                      working_hours: decimalHrs,
                      raw_working_hours: rawHrs,
                      earning: billedVal,
                      billed_amount: billedVal,
                      cost: costVal,
                      profit_margin: marginVal,
                      is_auto: Boolean(item.user_id),
                    };
                  });

                  // Restore draft custom line edits if present
                  const savedDraftLinesStr = invId ? sessionStorage.getItem(`re_draft_lines_${invId}`) : null;
                  let finalLines = mappedLines;
                  if (savedDraftLinesStr) {
                    try {
                      const savedDraft = JSON.parse(savedDraftLinesStr);
                      if (Array.isArray(savedDraft) && savedDraft.length === mappedLines.length) {
                        finalLines = savedDraft;
                      }
                    } catch (e) {}
                  }
                  setLines(finalLines);
                } else {
                  setLines([]);
                  setProjectUsers([]);
                }
                setLoadedInvoiceId(Number(invId));
              })
              .catch((err) => {
                console.error("Failed to parse invoice resources:", err);
                setLines([]);
                setProjectUsers([]);
                setLoadedInvoiceId(Number(invId));
                lastFetchedParamsRef.current = "";
              })
              .finally(() => {
                setIsLoadingDetails(false);
              });
          }
        } else {
          setLoadedInvoiceId(Number(invId));
          setIsLoadingDetails(false);
        }
      };

      // Check if invoice details were already fetched for this invoice ID
      if (lastInvoiceFetchIdRef.current === Number(invId) && fetchedInvoice) {
        doFetchResources(fetchedInvoice);
        return;
      }

      setIsLoadingDetails(true);
      lastInvoiceFetchIdRef.current = Number(invId);

      fetchInvoiceById(Number(invId))
        .unwrap()
        .then((invRes) => {
          if (invRes && invRes.status && invRes.data) {
            const invData = invRes.data;
            setFetchedInvoice(invData);

            // Update project selection and model if present
            if (invData.project_id || invData.project?.project_id) {
              const foundProjId = String(invData.project_id || invData.project?.project_id);
              setSelectedProjectId((prev) => prev || foundProjId);
            }
            const bType = (invData.budget_type || invData.project?.budget_type || "").toUpperCase();
            if (bType === "FIXED" || bType === "FIXED_COST") {
              setActiveModel("Fixed");
            } else if (
              bType === "CONTRACT" ||
              bType === "TIME_AND_MATERIAL" ||
              bType === "TIME AND MATERIAL" ||
              bType === "T&M"
            ) {
              setActiveModel("Contract");
            }

            // If the invoice has saved resources (allocated invoice from GET /invoices/{id})
            if (Array.isArray(invData.resources) && invData.resources.length > 0) {
              const sortedResources = [...invData.resources].sort(
                (a, b) => Number(a.invoice_resource_id || 0) - Number(b.invoice_resource_id || 0)
              );

              const resourceUsers = sortedResources.map((r) => ({
                user_id: Number(r.user_id),
                name: r.user_name,
                rate_per_hour: Number(r.rate_per_hour || 0),
                working_hours: parseFloat(r.hours || 0),
              }));

              setProjectUsers(resourceUsers);

              const allocatedLines = sortedResources.map((resItem, idx) => {
                let parsedLine = {};
                if (typeof resItem.line_items === "string") {
                  try {
                    parsedLine = JSON.parse(resItem.line_items);
                  } catch (e) {
                    parsedLine = {};
                  }
                } else if (typeof resItem.line_items === "object" && resItem.line_items !== null) {
                  parsedLine = resItem.line_items;
                }

                const billedVal = Number(resItem.earning ?? resItem.billed_amount ?? parsedLine.billed_amount ?? 0);
                const costVal = Number(resItem.cost ?? 0);
                const marginVal = Number(resItem.profit_margin !== undefined ? resItem.profit_margin : (billedVal - costVal));
                const hrsVal = parseFloat(resItem.hours ?? 0);

                return {
                  id: parsedLine.line_item_id || resItem.invoice_resource_id || `line-${idx}`,
                  line_item_id: parsedLine.line_item_id || String(resItem.invoice_resource_id || idx + 1),
                  line_item_name: parsedLine.line_item_name || resItem.line_item_name || "contract staffing services",
                  line_item_description: parsedLine.line_item_description || "",
                  user_id: resItem.user_id ? Number(resItem.user_id) : null,
                  resource_name: resItem.user_name || "Unassigned",
                  billing_type: resItem.billing_type || parsedLine.billing_type || "Billable",
                  working_hours: hrsVal,
                  extracted_hours: hrsVal,
                  raw_working_hours: resItem.hours,
                  cost: costVal,
                  earning: billedVal,
                  billed_amount: billedVal,
                  profit_margin: marginVal,
                  rate_per_hour: Number(resItem.rate_per_hour || 0),
                  is_auto: true,
                };
              });

              setLines(allocatedLines);
              setLoadedInvoiceId(Number(invId));
              setIsLoadingDetails(false);
              return;
            }

            doFetchResources(invData);
          } else {
            doFetchResources(invoice);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch invoice details:", err);
          setLoadedInvoiceId(Number(invId));
          setIsLoadingDetails(false);
          lastInvoiceFetchIdRef.current = null;
        });
    }
  }, [show, isInline, invoice?.id, invoice?.rawItem?.invoice_id, selectedProjectId, activeModel, parseInvoiceResources, fetchProjectResources, fetchInvoiceById]);

  // Auto-save draft lines to sessionStorage whenever lines change
  useEffect(() => {
    const invId = invoice?.rawItem?.invoice_id || invoice?.id || invoice?.invoice_id;
    if (invId && lines.length > 0) {
      try {
        sessionStorage.setItem(`re_draft_lines_${invId}`, JSON.stringify(lines));
      } catch (e) {}
    }
  }, [lines, invoice]);

  // Auto-save draft project users to sessionStorage whenever projectUsers change
  useEffect(() => {
    const invId = invoice?.rawItem?.invoice_id || invoice?.id || invoice?.invoice_id;
    if (invId && projectUsers.length > 0) {
      try {
        sessionStorage.setItem(`re_draft_users_${invId}`, JSON.stringify(projectUsers));
      } catch (e) {}
    }
  }, [projectUsers, invoice]);

  const allAvailableUsers = useMemo(() => {
    const userMap = new Map();

    const rateMap = new Map();
    const rateList = Array.isArray(ratePerHourResponse)
      ? ratePerHourResponse
      : Array.isArray(ratePerHourResponse?.data)
        ? ratePerHourResponse.data
        : [];
    rateList.forEach((r) => {
      const uId = Number(r.user_id || r.id);
      const rate = Number(r.per_hr_rate || r.rate_per_hour || r.rate || 0);
      if (uId && rate > 0) rateMap.set(uId, rate);
    });

    // 1. Project users with logs for this month (highest priority)
    if (Array.isArray(projectUsers)) {
      projectUsers.forEach((u) => {
        const uId = Number(u.user_id);
        if (uId) {
          userMap.set(uId, {
            user_id: uId,
            name: u.name,
            rate_per_hour: Number(u.rate_per_hour || rateMap.get(uId) || 0),
            working_hours: u.working_hours ?? 0,
            has_logs: true,
          });
        }
      });
    }

    // 2. Project-based team members
    const teamList = Array.isArray(projectTeamData)
      ? projectTeamData
      : Array.isArray(projectTeamData?.data)
        ? projectTeamData.data
        : [];
    teamList.forEach((u) => {
      const uId = Number(u.user_id || u.id);
      if (uId && !userMap.has(uId)) {
        userMap.set(uId, {
          user_id: uId,
          name: u.name || u.user_name || u.full_name,
          rate_per_hour: Number(u.rate_per_hour || rateMap.get(uId) || 0),
          working_hours: 0,
          is_project_member: true,
        });
      }
    });

    // 3. All portal users
    const allList = Array.isArray(allUsersResponse)
      ? allUsersResponse
      : Array.isArray(allUsersResponse?.data)
        ? allUsersResponse.data
        : [];
    allList.forEach((u) => {
      const uId = Number(u.user_id || u.id);
      if (uId && !userMap.has(uId)) {
        userMap.set(uId, {
          user_id: uId,
          name: u.name || u.user_name || u.full_name,
          rate_per_hour: Number(u.rate_per_hour || rateMap.get(uId) || 0),
          working_hours: 0,
        });
      }
    });

    return Array.from(userMap.values()).sort((a, b) => {
      return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
    });
  }, [projectUsers, projectTeamData, allUsersResponse, ratePerHourResponse]);

  const isAddingLineRef = useRef(false);

  const handleAddLine = useCallback((e) => {
    if (e) {
      if (typeof e.preventDefault === "function") e.preventDefault();
      if (typeof e.stopPropagation === "function") e.stopPropagation();
    }
    // Prevent double-click or duplicate trigger
    if (isAddingLineRef.current) return;
    isAddingLineRef.current = true;
    setTimeout(() => {
      isAddingLineRef.current = false;
    }, 400);

    setLines((prev) => {
      const nextNum = prev.length + 1;
      const uniqueId = `manual_line_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
      const newLine = {
        line_item_id: uniqueId,
        line_item_name: "contract staffing services",
        line_item_description: `Line ${nextNum} · Manually added line`,
        user_id: "",
        resource_name: "",
        name: "",
        extracted_hours: 0,
        working_hours: 0,
        raw_working_hours: 0,
        cost: 0,
        billing_type: "Billable",
        earning: 0,
        billed_amount: 0,
        profit_margin: 0,
        is_auto: false,
        is_manual_line: true,
      };
      toast.info(`Line ${nextNum} added!`);
      return [...prev, newLine];
    });
  }, []);

  const handleDeleteLine = useCallback((rowId, idx) => {
    setLines((prev) => {
      return prev.filter((item, i) => {
        const id = item.line_item_id || `line-${i}`;
        const isMatch = rowId !== undefined && rowId !== null
          ? String(id) === String(rowId)
          : i === idx;
        // Case A lines (timesheet logs) cannot be deleted!
        if (isMatch && item.is_auto && !item.is_manual_line) {
          toast.warning("Cannot delete timesheet logged resource.");
          return true;
        }
        return !isMatch;
      });
    });
    toast.info("Line removed.");
  }, []);

  const handleUserChange = (idx, userId) => {
    setLines((prev) => {
      const updated = [...prev];
      const numId = userId !== "" && userId !== undefined && userId !== null ? Number(userId) : "";

      if (numId !== "") {
        const isAlreadySelected = updated.some((l, lIdx) => lIdx !== idx && Number(l.user_id) === numId);
        if (isAlreadySelected) {
          const matched = allAvailableUsers.find((u) => Number(u.user_id) === numId);
          toast.warning(`Resource "${matched?.name || "This user"}" is already selected in another line.`);
          return prev;
        }
      }

      const target = { ...updated[idx], user_id: numId };
      const matchedUser = allAvailableUsers.find((u) => Number(u.user_id) === Number(numId));
      const isReimb = target.billing_type?.toLowerCase() === "reimbursement";
      const billed = Number(target.earning !== undefined ? target.earning : (target.billed_amount || 0));

      if (matchedUser) {
        target.resource_name = matchedUser.name;
        target.name = matchedUser.name;
        const rawUserHrs = matchedUser.working_hours !== undefined && matchedUser.working_hours !== null
          ? matchedUser.working_hours
          : 0;
        const decimalHrs = parseWorkingHoursToDecimal(rawUserHrs);
        target.working_hours = decimalHrs;
        target.raw_working_hours = rawUserHrs;

        const rate = Number(matchedUser.rate_per_hour || 0);
        target.rate_per_hour = rate;

        if (isReimb) {
          const cost = Number(target.cost !== undefined ? target.cost : billed);
          target.cost = cost;
          target.profit_margin = Math.round((billed - cost + Number.EPSILON) * 100) / 100;
        } else {
          const rawCost = decimalHrs * rate;
          const cost = Math.round((rawCost + Number.EPSILON) * 100) / 100;
          target.cost = cost;
          target.profit_margin = Math.round((billed - cost + Number.EPSILON) * 100) / 100;
        }
      } else {
        target.working_hours = 0;
        target.raw_working_hours = 0;
        target.rate_per_hour = 0;
        if (!isReimb) {
          target.cost = 0;
          target.profit_margin = billed;
        }
      }
      updated[idx] = target;
      return updated;
    });
  };

  const handleTypeChange = (idx, type) => {
    setLines((prev) => {
      const updated = [...prev];
      const target = { ...updated[idx], billing_type: type };
      const isReimb = type?.toLowerCase() === "reimbursement";
      const billed = Number(target.earning !== undefined ? target.earning : (target.billed_amount || 0));

      if (isReimb) {
        target.cost = billed;
        target.profit_margin = 0;
      } else {
        const matchedUser = projectUsers.find((u) => Number(u.user_id) === Number(target.user_id));
        const rawUserHrs = matchedUser && matchedUser.working_hours !== undefined && matchedUser.working_hours !== null
          ? matchedUser.working_hours
          : (target.working_hours ?? target.extracted_hours ?? 0);
        const decimalHrs = parseWorkingHoursToDecimal(rawUserHrs);
        target.working_hours = decimalHrs;
        target.raw_working_hours = rawUserHrs;
        const rate = Number(matchedUser?.rate_per_hour || 0);
        const rawCost = decimalHrs * rate;
        const cost = Math.round((rawCost + Number.EPSILON) * 100) / 100;
        target.cost = cost;
        target.profit_margin = Math.round((billed - cost + Number.EPSILON) * 100) / 100;
      }

      updated[idx] = target;
      return updated;
    });
  };

  const handleBilledChange = useCallback((idx, sanitized) => {
    const numVal = parseFloat(sanitized) || 0;

    setLines((prev) => {
      const updated = [...prev];
      const target = { ...updated[idx] };
      const cost = Number(target.cost || 0);
      const isReimb = target.billing_type?.toLowerCase() === "reimbursement";

      target.earning = numVal;
      target.billed_amount = numVal;
      target.raw_billed_input = sanitized;

      if (isReimb) {
        target.cost = numVal;
        target.profit_margin = 0;
      } else {
        target.profit_margin = Math.round((numVal - cost + Number.EPSILON) * 100) / 100;
      }

      updated[idx] = target;
      return updated;
    });
  }, []);

  const totals = useMemo(() => {
    let totalHrs = 0;
    let totalBilled = 0;
    let totalCost = 0;
    let totalMargin = 0;
    let hasAnyAssignedBillable = false;

    const hasUnassignedBillable = lines.some(
      (l) => l.billing_type?.toLowerCase() !== "reimbursement" && !l.user_id
    );

    lines.forEach((l) => {
      const isReimb = l.billing_type?.toLowerCase() === "reimbursement";
      const hasUser = Boolean(l.user_id);

      const billed = Number(l.earning !== undefined ? l.earning : (l.billed_amount || 0));
      totalBilled += billed;

      if (isReimb) {
        const cost = Number(l.cost || 0);
        const margin = Number(l.profit_margin !== undefined ? l.profit_margin : (billed - cost));
        totalCost += cost;
        totalMargin += margin;
      } else if (hasUser) {
        hasAnyAssignedBillable = true;
        totalHrs += parseWorkingHoursToDecimal(l.working_hours ?? l.extracted_hours ?? 0);
        const cost = Number(l.cost || 0);
        const margin = Number(l.profit_margin !== undefined ? l.profit_margin : (billed - cost));
        totalCost += cost;
        totalMargin += margin;
      }
    });

    const formattedTotalHrs = hasAnyAssignedBillable
      ? (totalHrs % 1 === 0 ? totalHrs : Number(totalHrs.toFixed(2)))
      : "—";

    const roundedBilled = Math.round((totalBilled + Number.EPSILON) * 100) / 100;
    const roundedCost = Math.round((totalCost + Number.EPSILON) * 100) / 100;
    const roundedMargin = Math.round((totalMargin + Number.EPSILON) * 100) / 100;

    const isTotalNegative = roundedMargin < 0;
    const ratioNum = roundedCost > 0 ? (roundedBilled / roundedCost).toFixed(2) : null;
    const ratio = !hasUnassignedBillable && ratioNum != null
      ? isTotalNegative ? `-${ratioNum}x` : `${ratioNum}x`
      : "—";

    return {
      totalHrs: formattedTotalHrs,
      totalBilled: roundedBilled,
      totalCost: roundedCost,
      totalMargin: roundedMargin,
      hasUnassignedBillable,
      ratio,
    };
  }, [lines]);

  const invTotalAmt = useMemo(() => {
    return Number(invoiceDetail?.total_amount || invoice?.total_amount || invoice?.amount || 0);
  }, [invoiceDetail, invoice]);

  const hasInvoiceAmt = invTotalAmt > 0;
  const isAmountMatching = hasInvoiceAmt ? Math.abs(totals.totalBilled - invTotalAmt) < 0.01 : true;
  const isAmountExceeded = hasInvoiceAmt && totals.totalBilled > invTotalAmt + 0.01;

  const handleConfirm = async () => {
    const invId = invoice?.rawItem?.invoice_id || invoice?.id || invoice?.invoice_id;
    if (!invId) {
      toast.error("Invoice ID is missing.");
      return;
    }

    const matched = allProjects.find(
      (p) =>
        String(p.project_id) === String(selectedProjectId) ||
        String(p.id) === String(selectedProjectId) ||
        p.title === selectedProjectId ||
        p.project_name === selectedProjectId
    );
    const chosenProjectId = matched?.project_id || matched?.id || selectedProjectId;
    const chosenProjectTitle =
      matched?.title || matched?.project_name || selectedProjectId || invoice?.project;

    if (!chosenProjectId) {
      toast.warning("Please select a project before allocating.");
      return;
    }

    const budgetInfo = getBudgetTypeInfo(matched?.budget_type);
    if (!budgetInfo.isAssigned) {
      toast.error(
        `Project "${chosenProjectTitle}" does not have a Budget Type assigned in Project Details. Please configure Budget Type before mapping.`
      );
      return;
    }

    const repMonth = getValidReportingMonth();
    const fullReportingMonth = repMonth.length === 7 ? `${repMonth}-01` : repMonth;

    // Validate that every line item has an assigned user_id
    const unassignedIdx = lines.findIndex((l) => !l.user_id);
    if (unassignedIdx !== -1) {
      toast.warning(
        `Line ${unassignedIdx + 1} does not have an assigned resource. Please select a resource for all lines.`
      );
      return;
    }

    // Validate that no resource is assigned more than once
    const userIdsSeen = new Set();
    for (let i = 0; i < lines.length; i++) {
      const uId = Number(lines[i].user_id);
      if (userIdsSeen.has(uId)) {
        const dupUser = allAvailableUsers.find((u) => Number(u.user_id) === uId);
        const dupName = dupUser?.name || lines[i].resource_name || lines[i].name || "A resource";
        toast.warning(
          `Resource "${dupName}" is selected more than once. Each resource can only be selected once.`
        );
        return;
      }
      userIdsSeen.add(uId);
    }

    // Validate that line items match invoice amount (if invoice has an amount > 0)
    if (hasInvoiceAmt && !isAmountMatching) {
      if (isAmountExceeded) {
        toast.warning(
          `Total billed (₹${totals.totalBilled.toLocaleString("en-IN", { minimumFractionDigits: 2 })}) exceeds invoice amount (₹${invTotalAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}). Please adjust resource amounts to match the invoice.`
        );
      } 
      else {
        toast.warning(
          `Total of lines (₹${totals.totalBilled.toLocaleString("en-IN", { minimumFractionDigits: 2 })}) must match the invoice total (₹${invTotalAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}).`
        );
      }
      return;
    }

    // Check if any line has 0 or empty billed amount
    const zeroBilledLines = lines.filter(
      (l) => Number(l.earning !== undefined ? l.earning : (l.billed_amount || 0)) <= 0
    );

    let warningHtml = "";
    if (zeroBilledLines.length > 0) {
      const names = zeroBilledLines
        .map((l) => l.name || l.resource_name || "Unassigned")
        .join(", ");
      warningHtml = `
        <div style="margin-top: 12px; padding: 10px 12px; background-color: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; text-align: left; font-size: 13px; color: #991b1b; line-height: 1.4;">
          <strong>⚠️ Notice:</strong> <strong>${zeroBilledLines.length}</strong> resource line(s) have <strong>₹0.00</strong> billed amount:<br/>
          <span style="font-weight: 500; color: #dc2626;">${names}</span><br/>
          <small style="color: #7f1d1d;">Did you forget to split the billed amount to these lines?</small>
        </div>
      `;
    }

    const confirmResult = await Swal.fire({
      title: zeroBilledLines.length > 0 ? "Review & Confirm Allocation" : "Confirm Allocation",
      html: `
        <div style="text-align: left; font-size: 13.5px; color: #374151;">
          <p style="margin-bottom: 8px;">Are you sure you want to allocate invoice <strong>${invoiceNumberStr || invoice?.invoice_number || ""}</strong>?</p>
          <div style="background-color: #f8fafc; padding: 10px 14px; border-radius: 6px; border: 1px solid #e2e8f0; font-size: 12.5px; line-height: 1.6;">
            <div><strong>Project:</strong> ${chosenProjectTitle}</div>
            <div><strong>Reporting Month:</strong> ${repMonth}</div>
            <div><strong>Total Invoice Amount:</strong> ₹${totals.totalBilled.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          ${warningHtml}
        </div>
      `,
      icon: zeroBilledLines.length > 0 ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Confirm & Allocate",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#04a189",
      cancelButtonColor: "#d33",
      reverseButtons: true,
      allowOutsideClick: false,
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    try {
      let response;
      if (isManualInvoice) {
        const totalBilledVal = totals.totalBilled || 1;
        const invoiceResources = lines.map((l) => {
          const earningNum = parseFloat(Number(l.earning !== undefined ? l.earning : (l.billed_amount || 0)).toFixed(2));
          const ratioVal = totalBilledVal > 0 ? parseFloat(((earningNum / totalBilledVal) * 100).toFixed(2)) : 0;
          return {
            user_id: l.user_id ? Number(l.user_id) : null,
            billing_type: l.billing_type || "Billable",
            earning: earningNum,
            ratio: ratioVal,
          };
        });

        const payload = {
          invoice_id: Number(invId),
          invoice_resources: invoiceResources,
        };

        console.log("Allocating manual contract invoice with payload:", payload);
        response = await allocateContractInvoice(payload).unwrap();
      } 
      else {
        const totalBilledVal = totals.totalBilled || 1;
        const resourcesPayload = lines.map((l) => {
          const earningNum = parseFloat(Number(l.earning !== undefined ? l.earning : (l.billed_amount || 0)).toFixed(2));
          const isManualLine = Boolean(
            l.is_manual_line ||
            !l.line_item_id ||
            String(l.line_item_id).startsWith("manual_line")
          );
          const ratioVal = totalBilledVal > 0 ? parseFloat(((earningNum / totalBilledVal) * 100).toFixed(2)) : 0;

          return {
            line_item_id: isManualLine ? null : (isNaN(Number(l.line_item_id)) ? l.line_item_id : Number(l.line_item_id)),
            user_id: l.user_id ? Number(l.user_id) : null,
            billing_type: l.billing_type || "Billable",
            earning: earningNum,
            ratio: ratioVal,
            reporting_month: repMonth || fullReportingMonth,
          };
        });

        const payload = {
          invoice_id: Number(invId),
          project_id: Number(chosenProjectId),
          reporting_month: repMonth || fullReportingMonth,
          budget_type: matched?.budget_type || "CONTRACT",
          resources: resourcesPayload,
        };

        console.log("Allocating synced invoice with payload:", payload);
        response = await allocateInvoice(payload).unwrap();
      }

      if (response && response.status) {
        if (invId) {
          sessionStorage.removeItem(`re_draft_lines_${invId}`);
          sessionStorage.removeItem(`re_draft_users_${invId}`);
        }
        toast.success(
          response.message ||
          `Invoice ${invoice?.invoice || ""} allocated successfully!`
        );
        if (onConfirmAllocate) {
          onConfirmAllocate({
            project: chosenProjectTitle,
            project_id: chosenProjectId,
            reporting_month: repMonth,
            lines,
            allocatedData: response.data,
          });
        }
        handleClose();
      } 
      else {
        toast.error(response?.message || "Failed to allocate invoice");
      }
    } 
    catch (err) {
      console.error("Allocation error:", err);
      toast.error(
        err?.data?.message || err?.message || "Failed to allocate invoice"
      );
    }
  };

  const formatReportingMonth = (rm) => {
    const ym = rm || getValidReportingMonth();
    try {
      if (typeof ym === "string" && ym.includes("-")) {
        const parts = ym.split("-");
        if (parts.length >= 2) {
          const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, 1);
          return format(d, "MMM yyyy");
        }
      }
      return ym;
    } 
    catch {
      return ym;
    }
  };

  const displayInvoiceDate = useMemo(() => {
    const rawDate =
      invoiceDetail?.invoiced_date ||
      invoiceDetail?.payment_date ||
      invoice?.rawItem?.invoiced_date ||
      invoice?.rawItem?.payment_date ||
      invoice?.payment_date ||
      invoice?.date ||
      invoice?.created_at;
    if (!rawDate) return "—";
    try {
      const d = new Date(rawDate);
      return isNaN(d.getTime()) ? rawDate : format(d, "dd MMM yyyy");
    } catch {
      return rawDate;
    }
  }, [invoiceDetail, invoice]);

  const displayAmount = useMemo(() => {
    const rawAmt =
      invoiceDetail?.total_amount ||
      invoice?.total_amount ||
      invoice?.amount ||
      invoice?.rawItem?.total_amount;
    if (rawAmt === undefined || rawAmt === null || rawAmt === "") return "0.00";
    const num = Number(String(rawAmt).replace(/,/g, ""));
    if (isNaN(num)) return String(rawAmt);
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [invoiceDetail, invoice]);

  const displayReportingMonth = useMemo(() => {
    const rawRm =
      invoiceDetail?.reporting_month ||
      invoice?.rawItem?.reporting_month ||
      invoice?.reporting_month;
    return formatReportingMonth(rawRm);
  }, [invoiceDetail, invoice]);

  // Format exactly the 2nd line of each dashed box as in Mockup (Image 2)
  const formatBoxSecondLine = (box) => {
    const matchedUser = projectUsers.find((u) => Number(u.user_id) === Number(box.user_id));
    const resourceName =
      matchedUser?.name || box.resource_name || (box.user_id ? `User #${box.user_id}` : "Unassigned");
    const isReimb = box.billing_type?.toLowerCase() === "reimbursement";

    if (isReimb) {
      const rawDesc = box.line_item_description || "";
      const typeMatch = rawDesc.match(/reimbursement\s*\(([^)]+)\)/i) || rawDesc.match(/type:\s*([^·\n]+)/i);
      const detail = typeMatch ? typeMatch[1].trim() : "travel + lunch";
      return `→ Resource: ${resourceName} · Type: reimbursement (${detail})`;
    }

    const rawDesc = box.line_item_description || "";
    let monthStr = "";

    const descParts = rawDesc
      .split("\n")
      .map((s) => s.trim().replace(/^→\s*/, "").replace(/,$/, ""))
      .filter(Boolean);

    descParts.forEach((p) => {
      const lower = p.toLowerCase();
      if (lower.includes("month of")) {
        const match = p.match(/month of\s+([a-zA-Z0-9\s]+)/i);
        if (match) {
          const rawM = match[1].trim();
          monthStr = `Month: ${rawM.charAt(0).toUpperCase() + rawM.slice(1)}`;
        }
      }
    });

    if (!monthStr) {
      monthStr = `Month: ${formatReportingMonth(invoiceDetail?.reporting_month || invoice?.rawItem?.reporting_month || invoice?.reporting_month)}`;
    }

    const hasUser = Boolean(box.user_id);
    if (!hasUser) {
      return `→ Resource: Unassigned · ${monthStr} · Worked — hrs`;
    }

    const rawHrs = box.working_hours ?? box.extracted_hours ?? 0;
    const decimalHrs = parseWorkingHoursToDecimal(rawHrs);
    const formattedHrs = decimalHrs % 1 === 0 ? decimalHrs : Number(decimalHrs.toFixed(2));
    const hoursStr = `Worked ${formattedHrs} hrs`;

    return `→ Resource: ${resourceName} · ${monthStr} · ${hoursStr}`;
  };

  const gridRows = useMemo(() => {
    return lines.map((item, idx) => ({
      id: item.line_item_id || `line-${idx}`,
      idx,
      ...item,
    }));
  }, [lines]);

  const resourceColumns = useMemo(
    () => [
      {
        field: "resource",
        headerName: "Resource",
        flex: 1,
        width: 320,
        minWidth: 320,
        sortable: false,
        renderCell: (params) => {
          const item = params.row;
          const idx = params.row.idx;
          const isAuto = Boolean(item.is_auto);
          const selectedUser = allAvailableUsers.find((u) => Number(u.user_id) === Number(item.user_id)) || null;

          if (isAllocated) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", height: "100%" }}>
                <span style={{ fontSize: "14px", color: isDarkMode ? "#f2f1f1" : "#1e293b", fontWeight: 500 }}>
                  {selectedUser?.name || item.resource_name || item.name || "—"}
                </span>
                {!isManualInvoice && isAuto && (
                  <span className="badge bg-success-subtle text-success ms-1" style={{ fontSize: "11px" }}>
                    auto
                  </span>
                )}
              </Box>
            );
          }

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                height: "100%",
              }}
            >
              <Autocomplete
                size="small"
                options={allAvailableUsers}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, value) => Number(option.user_id) === Number(value?.user_id)}
                getOptionDisabled={(option) => {
                  if (!option?.user_id) return false;
                  const optId = Number(option.user_id);
                  const currentId = Number(item.user_id);
                  if (optId === currentId) return false;
                  return lines.some((l, lIdx) => lIdx !== idx && Number(l.user_id) === optId);
                }}
                value={selectedUser}
                onChange={(event, newValue) => {
                  handleUserChange(idx, newValue ? newValue.user_id : "");
                }}
                PopperComponent={(popperProps) => (
                  <Popper
                    {...popperProps}
                    placement="bottom-start"
                    modifiers={[
                      {
                        name: "flip",
                        enabled: true,
                        options: {
                          fallbackPlacements: ["top-start", "bottom-start"],
                          padding: 8,
                        },
                      },
                      {
                        name: "preventOverflow",
                        enabled: true,
                        options: {
                          boundary: "viewport",
                          padding: 8,
                        },
                      },
                    ]}
                    sx={{ zIndex: 1700 }}
                  />
                )}
                ListboxProps={{
                  sx: {
                    maxHeight: "216px !important",
                    overflowY: "auto",
                  },
                }}
                renderInput={(inputParams) => (
                  <TextField
                    {...inputParams}
                    placeholder="Select & search resource..."
                    size="small"
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: "14px",
                        height: "36px !important",
                        minHeight: "36px !important",
                        boxSizing: "border-box",
                        padding: "0 8px !important",
                        backgroundColor: isDarkMode ? "#1e293b" : "#fff",
                        color: isDarkMode ? "#f2f1f1" : "#1e293b",
                        "& fieldset": {
                          borderColor: isDarkMode ? "#475569" : "#cbd5e1",
                        },
                        "&:hover fieldset": {
                          borderColor: isDarkMode ? "#94a3b8" : "#94a3b8",
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "4px 4px !important",
                        fontSize: "14px",
                        color: isDarkMode ? "#f2f1f1" : "#1e293b",
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const optId = Number(option.user_id);
                  const currentId = Number(item.user_id);
                  const isAlreadySelected = optId !== currentId && lines.some((l, lIdx) => lIdx !== idx && Number(l.user_id) === optId);

                  return (
                    <li
                      {...props}
                      key={option.user_id}
                      style={{
                        fontSize: "14px",
                        minHeight: "36px",
                        padding: "6px 12px",
                        fontFamily: "inherit",
                        cursor: isAlreadySelected ? "not-allowed" : "pointer",
                        opacity: isAlreadySelected ? 0.55 : 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: isAlreadySelected
                          ? (isDarkMode ? "rgba(255, 255, 255, 0.04)" : "#f1f5f9")
                          : undefined,
                      }}
                    >
                      <Tooltip
                        title={isAlreadySelected ? "This resource is already selected in another line" : ""}
                        placement="right"
                        arrow
                        disableHoverListener={!isAlreadySelected}
                      >
                        <span
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                            pointerEvents: "auto",
                          }}
                        >
                          <span style={{ fontWeight: isAlreadySelected ? 400 : 500 }}>{option.name}</span>
                          {isAlreadySelected && (
                            <span
                              style={{
                                fontSize: "11px",
                                fontStyle: "italic",
                                color: isDarkMode ? "#94a3b8" : "#64748b",
                                marginLeft: "8px",
                              }}
                            >
                              (Already selected)
                            </span>
                          )}
                        </span>
                      </Tooltip>
                    </li>
                  );
                }}
                sx={{
                  width: 300,
                  minWidth: 300,
                  height: "36px",
                  my: "auto",
                  "& .MuiFormControl-root": {
                    height: "36px",
                    my: "auto",
                  },
                }}
              />
              {!isManualInvoice && isAuto && (
                <span className="badge bg-success-subtle text-success ms-1" style={{ fontSize: "11px" }}>
                  auto
                </span>
              )}
            </Box>
          );
        },
      },
      {
        field: "billing_type",
        headerName: "Type",
        width: 155,
        minWidth: 155,
        sortable: false,
        renderCell: (params) => {
          const item = params.row;
          const idx = params.row.idx;
          return (
            <FormControl size="small" sx={{ width: 135, minWidth: 135 }}>
              <Select
                value={item.billing_type || "Billable"}
                onChange={(e) => handleTypeChange(idx, e.target.value)}
                disabled={isAllocated}
                size="small"
                IconComponent={isAllocated ? () => null : ExpandMore}
                MenuProps={{
                  sx: { zIndex: 1600 },
                  PaperProps: {
                    sx: { zIndex: 1600, minWidth: "140px" },
                  },
                }}
                renderValue={(selected) => (
                  <span style={{ fontSize: "14px" }}>{selected}</span>
                )}
                sx={{
                  fontSize: "14px",
                  height: "36px",
                  width: 135,
                  backgroundColor: isAllocated ? "#f8fafc" : "#fff",
                  cursor: isAllocated ? "default" : "pointer",
                  "& .MuiSelect-select": {
                    paddingTop: "6px !important",
                    paddingBottom: "6px !important",
                    paddingLeft: "10px !important",
                    paddingRight: isAllocated ? "10px !important" : "24px !important",
                    display: "flex",
                    alignItems: "center",
                    color: "#1e293b",
                    WebkitTextFillColor: "#1e293b !important",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#f8fafc",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e2e8f0",
                    },
                  },
                }}
              >
                <MenuItem value="Billable">
                  Billable
                </MenuItem>
                <MenuItem value="Reimbursement">
                  Reimbursement
                </MenuItem>
              </Select>
            </FormControl>
          );
        },
      },
      {
        field: "hrs",
        headerName: "Hrs",
        width: 85,
        minWidth: 85,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => {
          const isReimbursement = params.row.billing_type?.toLowerCase() === "reimbursement";
          const hasUser = Boolean(params.row.user_id);
          if (isReimbursement || !hasUser) {
            return (
              <span style={{ fontSize: "13px", color: "#94a3b8" }}>—</span>
            );
          }
          const rawHrs = params.row.working_hours ?? params.row.extracted_hours ?? 0;
          const decimalHrs = parseWorkingHoursToDecimal(rawHrs);
          const formattedHrs = decimalHrs % 1 === 0 ? decimalHrs : Number(decimalHrs.toFixed(2));
          return (
            <span style={{ fontSize: "13px", color: "#334155", fontVariantNumeric: "tabular-nums" }}>
              {params.row.raw_working_hours !== undefined && params.row.raw_working_hours !== null && String(params.row.raw_working_hours).trim() !== ""
                ? String(params.row.raw_working_hours)
                : formattedHrs}
            </span>
          );
        },
      },
      {
        field: "billed",
        headerName: "Billed",
        width: 140,
        minWidth: 140,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => {
          const idx = params.row.idx;
          const billedVal = Number(params.row.earning ?? params.row.billed_amount ?? 0);

          if (isAllocated) {
            return (
              <span style={{ fontSize: "13px", color: "#334155", fontVariantNumeric: "tabular-nums" }}>
                ₹{billedVal.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            );
          }

          const rawVal = params.row.raw_billed_input !== undefined
            ? params.row.raw_billed_input
            : (billedVal > 0 ? String(billedVal) : "");

          return (
            <BilledCellInput
              idx={idx}
              value={rawVal}
              onChange={handleBilledChange}
              isDarkMode={isDarkMode}
            />
          );
        },
      },
      {
        field: "cost",
        headerName: "Cost",
        width: 130,
        minWidth: 130,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => {
          const isReimbursement = params.row.billing_type?.toLowerCase() === "reimbursement";
          const hasUser = Boolean(params.row.user_id);
          if (!isReimbursement && !hasUser) {
            return (
              <span style={{ fontSize: "13px", color: "#94a3b8" }}>—</span>
            );
          }
          return (
            <span style={{ fontSize: "13px", color: "#334155", fontVariantNumeric: "tabular-nums" }}>
              {Number(params.row.cost || 0).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          );
        },
      },
      {
        field: "margin",
        headerName: "Margin",
        width: 130,
        minWidth: 130,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => {
          const isReimbursement = params.row.billing_type?.toLowerCase() === "reimbursement";
          const hasUser = Boolean(params.row.user_id);
          if (!isReimbursement && !hasUser) {
            return (
              <span style={{ fontSize: "13px", color: "#94a3b8" }}>—</span>
            );
          }
          const billed = Number(params.row.earning ?? params.row.billed_amount ?? 0);
          const cost = Number(params.row.cost || 0);
          const margin = Number(params.row.profit_margin !== undefined ? params.row.profit_margin : (billed - cost));
          const isNegativeMargin = margin < 0;
          return (
            <span
              style={{
                fontSize: "13px",
                color: isNegativeMargin
                  ? isDarkMode ? "#f87171" : "#dc2626"
                  : isDarkMode ? "#f1f5f9" : "#334155",
                fontWeight: isNegativeMargin ? 600 : 400,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {margin.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          );
        },
      },
      {
        field: "ratio",
        headerName: "Ratio",
        width: 90,
        minWidth: 90,
        headerAlign: "center",
        align: "center",
        sortable: false,
        renderCell: (params) => {
          const isReimbursement = params.row.billing_type?.toLowerCase() === "reimbursement";
          const hasUser = Boolean(params.row.user_id);
          if (isReimbursement) {
            return (
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 400,
                  color: "#94a3b8",
                }}
              >
                excluded
              </span>
            );
          }
          if (!hasUser) {
            return (
              <span style={{ fontSize: "13px", fontWeight: 400, color: "#94a3b8" }}>
                —
              </span>
            );
          }
          const billed = Number(params.row.earning ?? params.row.billed_amount ?? 0);
          const cost = Number(params.row.cost || 0);
          const margin = Number(params.row.profit_margin !== undefined ? params.row.profit_margin : (billed - cost));
          const isNegative = margin < 0;
          const ratioVal = cost > 0 ? (billed / cost) : null;
          const ratio = ratioVal != null
            ? isNegative ? `-${ratioVal.toFixed(2)}x` : `${ratioVal.toFixed(2)}x`
            : "—";
          return (
            <span
              style={{
                fontSize: "13px",
                fontWeight: 700,
                color: isNegative
                  ? isDarkMode ? "#f87171" : "#dc2626"
                  : isDarkMode ? "#4ade80" : "#16a34a",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {ratio}
            </span>
          );
        },
      },
      ...(!isAllocated
        ? [
            {
              field: "action",
              headerName: "",
              width: 50,
              minWidth: 50,
              maxWidth: 50,
              headerAlign: "center",
              align: "center",
              sortable: false,
              renderCell: (params) => {
                // CASE A (Logs pota users) -> NO DELETE ICON!
                const isManualLine = Boolean(params.row.is_manual_line || !params.row.is_auto);
                if (!isManualLine) {
                  return null;
                }
                const idx = params.row.idx;
                const rowId = params.row.id;
                return (
                  <button
                    type="button"
                    tabIndex={-1}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onMouseUp={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setTimeout(() => {
                        handleDeleteLine(rowId, idx);
                      }, 0);
                    }}
                    title="Remove manually added line"
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: isDarkMode ? "#f87171" : "#ef4444",
                      borderRadius: "4px",
                      transition: "all 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = isDarkMode
                        ? "rgba(239, 68, 68, 0.2)"
                        : "#fee2e2";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <DeleteOutline sx={{ fontSize: 18 }} />
                  </button>
                );
              },
            },
          ]
        : []),
    ],
    [allAvailableUsers, isAllocated, isDarkMode, handleBilledChange, handleDeleteLine, handleUserChange, handleTypeChange, lines]
  );

  const fixedCostColumns = useMemo(
    () => [
      {
        field: "invoice",
        headerName: "Invoice",
        flex: 1.2,
        minWidth: 140,
        renderCell: (params) => (
          <span style={{ color: "#374151", fontSize: "13px" }}>
            {params.value}
          </span>
        ),
      },
      {
        field: "source",
        headerName: "Source",
        flex: 0.9,
        minWidth: 100,
        renderCell: (params) => {
          const src = params.value;
          if (src === "MANUAL" || src === "MANINV") {
            return <span className="badge-source-manual">MANUAL</span>;
          }
          return (
            <span className="badge bg-light text-dark border">
              {src || "TT"}
            </span>
          );
        },
      },
      {
        field: "date",
        headerName: "Date",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <span style={{ color: "#374151", fontSize: "13px" }}>{params.value}</span>
        ),
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 160,
        minWidth: 160,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => (
          <span
            style={{
              color: "#374151",
              fontSize: "13px",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {params.value}
          </span>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        minWidth: 140,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          if (params.row.isCurrent) {
            return (
              <span
                style={{
                  backgroundColor: "#06b6d4",
                  color: "#ffffff",
                  fontWeight: 500,
                  fontSize: "12px",
                  padding: "2px 12px",
                  height: "24px",
                  lineHeight: "24px",
                  borderRadius: "9999px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                }}
              >
                this one
              </span>
            );
          }
          return <span className="badge-status-mapped">Mapped</span>;
        },
      },
    ],
    []
  );

  const fixedCostRows = useMemo(() => {
    const currentInvoiceNumber =
      invoiceDetail?.invoice_number ||
      invoice?.invoice_number ||
      invoice?.invoice ||
      "INV-002419";
    const currentSource = isManualInvoice ? "MANUAL" : (invoiceDetail?.source || invoice?.source || "TT");
    const currentDate = displayInvoiceDate;
    const currentAmountFmt = displayAmount;
    const currentNumAmt = Number(String(invoiceDetail?.total_amount || invoice?.amount || invoice?.rawItem?.total_amount || 0).replace(/,/g, "")) || 0;

    // Map previous invoices fetched via API
    const previousRows = (prevInvoices || []).map((inv, idx) => {
      const invDate = inv.invoiced_date || inv.payment_date || inv.date;
      let formattedDate = "—";
      if (invDate) {
        try {
          const d = new Date(invDate);
          formattedDate = isNaN(d.getTime()) ? invDate : format(d, "dd MMM yyyy");
        } catch {
          formattedDate = invDate;
        }
      }
      const rawAmt = inv.total_amount ?? inv.amount ?? 0;
      const numAmt = Number(String(rawAmt).replace(/,/g, "")) || 0;
      const rawStatus = inv.status || "mapped";
      const formattedStatus = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase();

      return {
        id: `receipt-prev-${inv.invoice_id || idx}`,
        invoice_id: inv.invoice_id,
        invoice: inv.invoice_number || `INV-${inv.invoice_id}` || "",
        source: inv.source || "TT",
        date: formattedDate,
        amount: numAmt.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        rawAmount: numAmt,
        status: formattedStatus,
        isCurrent: false,
      };
    });

    // Check if the current invoice is already in the fetched previous list
    const existingIndex = previousRows.findIndex(
      (r) =>
        (currentInvoiceId && r.invoice_id === currentInvoiceId) ||
        (currentInvoiceNumber && r.invoice === currentInvoiceNumber)
    );

    if (existingIndex !== -1) {
      previousRows[existingIndex] = {
        ...previousRows[existingIndex],
        isCurrent: true,
      };
      return previousRows;
    }

    const currentRow = {
      id: "receipt-current",
      invoice: currentInvoiceNumber,
      source: currentSource,
      date: currentDate,
      amount: currentAmountFmt,
      rawAmount: currentNumAmt,
      status: "this one",
      isCurrent: true,
    };

    return [...previousRows, currentRow];
  }, [
    prevInvoices,
    invoiceDetail,
    invoice,
    currentInvoiceId,
    isManualInvoice,
    displayInvoiceDate,
    displayAmount,
  ]);

  const totalReceivedAmount = useMemo(() => {
    return fixedCostRows.reduce((sum, r) => sum + (r.rawAmount || 0), 0);
  }, [fixedCostRows]);

  const renderHeader = () => (
    <div className="d-flex align-items-center gap-2">
      {isInline && (
        <button
          type="button"
          className="R_btn me-2"
          onClick={handleClose}
          title="Back to Invoice Queue"
          style={{
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            border: isDarkMode ? "1px solid #444" : "1px solid #e2e8f0",
            backgroundColor: isDarkMode ? "#2a2a2a" : "#fff",
            color: isDarkMode ? "#f2f1f1" : "#475569",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = isDarkMode ? "#333" : "#f1f5f9";
            e.currentTarget.style.borderColor = isDarkMode ? "#555" : "#cbd5e1";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = isDarkMode ? "#2a2a2a" : "#fff";
            e.currentTarget.style.borderColor = isDarkMode ? "#444" : "#e2e8f0";
          }}
        >
          <IoIosArrowBack size={20} />
        </button>
      )}
      <span className={`fs-6 fw-bold m-0 ${isDarkMode ? "text-white" : ""}`}>
        {isAllocated
          ? `Allocated Details · ${invoiceDetail?.invoice_number || invoice?.invoice}`
          : isMapped
            ? `Mapped Details · ${invoiceDetail?.invoice_number || invoice?.invoice}`
            : `Map ${invoiceDetail?.invoice_number || invoice?.invoice}`} · ₹{invoiceDetail?.total_amount ? Number(invoiceDetail.total_amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (invoice?.amount || "0.00")}
      </span>
      {isManualInvoice ? (
        <span className="badge-source-manual">MANUAL</span>
      ) : (
        <span
          className="badge"
          style={{
            backgroundColor: isDarkMode ? "#334155" : "#f1f5f9",
            color: isDarkMode ? "#f1f5f9" : "#334155",
            border: isDarkMode ? "1px solid #475569" : "1px solid #cbd5e1",
          }}
        >
          {invoiceDetail?.source || invoice?.source || "TT"}
        </span>
      )}
      {hasProjectSelected && activeModel === "Fixed" ? (
        <span className="badge-model-fixed">
          Fixed Cost
        </span>
      ) : hasProjectSelected && activeModel === "Contract" ? (
        <span className="badge-model-contract">
          {(rawType?.toUpperCase() === "TIME_AND_MATERIAL" ||
            rawType?.toUpperCase() === "TIME AND MATERIAL" ||
            rawType?.toUpperCase() === "T&M")
            ? "T&M(Contract)"
            : "Contract"}
        </span>
      ) : null}
    </div>
  );

  const renderBody = () => (
    <>
      {/* Top Banner - only shown when a project is selected */}
      {hasProjectSelected && activeModel === "Fixed" && (
        <div
          className="alert alert-info py-2 px-3 small border-0 mb-3"
          style={{ backgroundColor: "#eff6ff", color: "#1e40af", borderRadius: "8px" }}
        >
          Mapping this invoice sets it to <strong>Mapped</strong>. Resource allocation for this project happens once, when the project is marked complete.
        </div>
      )}

      {/* Project Metadata */}
      <div className="row mb-3">
        <div className="col-12 col-md-6">
          <label htmlFor="project_id" className="form-label">
            Select project name <span className="text-rgbr-red">*</span>
          </label>
          <ProjectSelectAutocomplete
            id="project_id"
            projects={allProjects}
            value={selectedProjectId}
            onChange={(newId) => handleProjectSelect(newId)}
            disabled={isProjectLocked}
            placeholder="Select & search project..."
            existingProjectsMap={existingProjectsMap}
            isDarkMode={isDarkMode}
          />
        </div>

        {activeModel === "Fixed" ? (
          <>
            <div className="col-6 col-md-3">
              <label className="form-label" style={{ color: isDarkMode ? "#cbd5e1" : undefined }}>Invoice Date</label>
              <div
                className="form-control"
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: isDarkMode ? "#1e293b" : "#f8f9fa",
                  color: isDarkMode ? "#f1f5f9" : "#1e293b",
                  height: "38px",
                  borderRadius: "4px",
                  borderColor: isDarkMode ? "#475569" : "#d1d5db",
                  fontSize: "13px",
                }}
              >
                {displayInvoiceDate}
              </div>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label" style={{ color: isDarkMode ? "#cbd5e1" : undefined }}>Amount</label>
              <div
                className="form-control"
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: isDarkMode ? "#1e293b" : "#f8f9fa",
                  color: isDarkMode ? "#f1f5f9" : "#1e293b",
                  height: "38px",
                  borderRadius: "4px",
                  borderColor: isDarkMode ? "#475569" : "#d1d5db",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 600,
                  fontSize: "13px",
                }}
              >
                ₹{displayAmount}
              </div>
            </div>
          </>
        ) : (
          hasProjectSelected && selectedBudgetInfo.isAssigned && (
            <>
              <div className="col-6 col-md-3">
                <label className="form-label" style={{ color: isDarkMode ? "#cbd5e1" : undefined }}>Invoice Date</label>
                <div
                  className="form-control"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: isDarkMode ? "#1e293b" : "#f8f9fa",
                    color: isDarkMode ? "#f1f5f9" : "#1e293b",
                    height: "38px",
                    borderRadius: "4px",
                    borderColor: isDarkMode ? "#475569" : "#d1d5db",
                    fontSize: "13px",
                  }}
                >
                  {displayInvoiceDate}
                </div>
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label" style={{ color: isDarkMode ? "#cbd5e1" : undefined }}>Reporting Month</label>
                <div
                  className="form-control"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: isDarkMode ? "#1e293b" : "#f8f9fa",
                    color: isDarkMode ? "#f1f5f9" : "#1e293b",
                    height: "38px",
                    borderRadius: "4px",
                    borderColor: isDarkMode ? "#475569" : "#d1d5db",
                    fontSize: "13px",
                  }}
                >
                  <span>{displayReportingMonth}</span>
                  <span className="small ms-1" style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}>— from invoice</span>
                </div>
              </div>
            </>
          )
        )}
      </div>

      {/* Show prompt if no project is selected yet */}
      {!hasProjectSelected ? (
        <div
          className="p-4 text-center rounded my-4"
          style={{
            backgroundColor: isDarkMode ? "#18181b" : "#f9fafb",
            border: isDarkMode ? "1px dashed #3f3f46" : "1px dashed #cbd5e1",
            color: isDarkMode ? "#a1a1aa" : "#64748b",
          }}
        >
          <div className="fw-semibold mb-1" style={{ fontSize: "14px", color: isDarkMode ? "#f4f4f5" : "#334155" }}>
            Please select a project
          </div>
          <div className="small" style={{ color: isDarkMode ? "#a1a1aa" : "#64748b" }}>
            Select a project from the dropdown above to view and configure mapping details.
          </div>
        </div>
      ) : !selectedBudgetInfo.isAssigned ? (
        <div
          className="p-4 text-center rounded my-4"
          style={{
            backgroundColor: isDarkMode ? "rgba(245, 158, 11, 0.12)" : "#fffbeb",
            border: isDarkMode ? "1px dashed rgba(245, 158, 11, 0.4)" : "1px dashed #fcd34d",
            color: isDarkMode ? "#fcd34d" : "#92400e",
          }}
        >
          <div className="fw-semibold mb-1" style={{ fontSize: "14px", color: isDarkMode ? "#fbbf24" : "#b45309" }}>
            ⚠️ No Budget Type Assigned
          </div>
          <div className="small" style={{ color: isDarkMode ? "#fcd34d" : "#92400e" }}>
            Project <strong>"{matchedProjectObj?.title || matchedProjectObj?.name || selectedProjectId}"</strong> does not have a Budget Type assigned in Project Details. Please configure Budget Type (Fixed Cost, Contract, or Time & Material) in Project Details before mapping.
          </div>
        </div>
      ) : activeModel === "Fixed" ? (
        <>
          {/* Receipts on this project table (MUI DataGrid identical to Invoice Queue table) */}
          <div className="d-flex align-items-center gap-2 mt-3 mb-2">
            <span
              style={{
                width: "9px",
                height: "9px",
                borderRadius: "50%",
                backgroundColor: "#10b981",
                display: "inline-block",
              }}
            />
            <span className="fw-semibold" style={{ fontSize: "14px" }}>
              Receipts mapped to this project so far
            </span>
          </div>
          <Box
            sx={{
              width: "100%",
              mt: 1,
              mb: 3,
              backgroundColor: isDarkMode ? "transparent" : "#fff",
              border: isDarkMode ? "1px solid #444" : "1px solid #e0e0e0",
              borderRadius: "6px",
              overflow: "hidden",
            }}
          >
            <DataGrid
              rows={fixedCostRows}
              columns={fixedCostColumns}
              autoHeight
              hideFooterPagination
              disableRowSelectionOnClick
              disableColumnMenu
              getRowId={(row) => row.id}
              rowHeight={48}
              slots={{
                footer: () => (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      borderTop: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
                      backgroundColor: isDarkMode ? "#1e1e1e" : "#f8fafc",
                      minHeight: "48px",
                      px: 0,
                      width: "100%",
                      fontWeight: 700,
                      fontSize: "13px",
                      color: isDarkMode ? "#f2f1f1" : "#1e293b",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 390,
                        fontWeight: 700,
                        fontSize: "13px",
                        color: isDarkMode ? "#f2f1f1" : "#1e293b",
                        pl: "16px",
                      }}
                    >
                      Total received
                    </Box>
                    <Box
                      sx={{
                        width: 160,
                        minWidth: 160,
                        maxWidth: 160,
                        flexShrink: 0,
                        textAlign: "right",
                        pr: "10px",
                        fontWeight: 700,
                        fontSize: "13px",
                        color: isDarkMode ? "#f2f1f1" : "#111827",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {totalReceivedAmount.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Box>
                    <Box
                      sx={{
                        width: 140,
                        minWidth: 140,
                        maxWidth: 140,
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                ),
                noRowsOverlay: CustomNoRowsOverlay,
              }}
              sx={{
                border: "none !important",
                borderRadius: 0,
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
                  borderBottom: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: isDarkMode ? "#f2f1f1" : "#6b7280",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 600,
                },
                "& .MuiDataGrid-cell": {
                  fontSize: "13px",
                  borderBottom: isDarkMode ? "1px solid #2a2a2a" : "1px solid #f0f0f0",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                  outline: "none !important",
                },
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                  outline: "none !important",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: isDarkMode ? "#2a2a2a !important" : "#fafafa",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
                  minHeight: "48px",
                  backgroundColor: isDarkMode ? "#1e1e1e" : "transparent",
                },
              }}
            />
          </Box>

          {/* KPI Cards */}
          <div className="row g-2 mb-2">
            <div className="col-6 col-md-3">
              <div className={`p-2 border rounded text-center ${isDarkMode ? "bg-dark border-secondary text-white" : "bg-light"}`}>
                <div className="text-muted small" style={{ fontSize: "10px" }}>
                  CONTRACT VALUE
                </div>
                <div className="fw-bold" style={{ color: isDarkMode ? "#f2f1f1" : "inherit" }}>
                  ₹{Number(
                    (prevInvoicesSummary?.budget_amount != null
                      ? prevInvoicesSummary.budget_amount
                      : matchedProjectObj?.budget || matchedProjectObj?.contract_value || matchedProjectObj?.budget_amount || 0)
                  ).toLocaleString("en-IN")}
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className={`p-2 border rounded text-center ${isDarkMode ? "bg-dark border-secondary text-white" : "bg-light"}`}>
                <div className="text-muted small" style={{ fontSize: "10px" }}>
                  RECEIVED SO FAR
                </div>
                <div className="fw-bold text-success">
                  ₹{totalReceivedAmount.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className={`p-2 border rounded text-center ${isDarkMode ? "bg-dark border-secondary text-white" : "bg-light"}`}>
                <div className="text-muted small" style={{ fontSize: "10px" }}>
                  COST TO DATE
                </div>
                <div className="fw-bold" style={{ color: isDarkMode ? "#f2f1f1" : "inherit" }}>
                  ₹{Number(
                    prevInvoicesSummary?.pending_amount != null
                      ? prevInvoicesSummary.pending_amount
                      : matchedProjectObj?.cost_to_date || 0
                  ).toLocaleString("en-IN")}
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className={`p-2 border rounded text-center ${isDarkMode ? "bg-dark border-secondary text-white" : "bg-light"}`}>
                <div className="text-muted small" style={{ fontSize: "10px" }}>
                  PROJECT STATUS
                </div>
                <div className={`fw-bold ${isDarkMode ? "text-info" : "text-primary"}`}>
                  {matchedProjectObj?.status || "In Progress"}
                </div>
              </div>
            </div>
          </div>

          <div className="text-muted small mb-3" style={{ fontSize: "12px" }}>
            The Allocate button is enabled only when the project is marked complete. Until then this project contributes cost, but no earnings, to the monthly dashboard.
          </div>
        </>
      ) : isLoadingResources ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            my: 3,
            backgroundColor: isDarkMode ? "#1e1e1e" : "#f8fafc",
            border: isDarkMode ? "1px dashed #444" : "1px dashed #cbd5e1",
            borderRadius: "8px",
            gap: 2,
          }}
        >
          <CircularProgress size={38} sx={{ color: "rgb(4, 161, 137)" }} />
          <Box sx={{ fontSize: "14px", fontWeight: 500, color: isDarkMode ? "#cbd5e1" : "#475569" }}>
            Loading line items and resource allocations...
          </Box>
        </Box>
      ) : (
        <>
          {isManualInvoice ? (
            <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: "#04a189",
                    display: "inline-block",
                  }}
                ></span>
                <span className="fw-semibold" style={{ fontSize: "13px" }}>
                  Project Resources
                </span>
              </div>
              {!isAllocated && (
                <button
                  type="button"
                  className="re-btn-sync"
                  style={{
                    fontSize: "12px",
                    height: "28px",
                    padding: "0 10px",
                    gap: "4px",
                  }}
                  onClick={handleAddLine}
                >
                  + Add line
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Parsed Line Items Header and Boxes */}
              <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
                <div className="d-flex align-items-center gap-2">
                  <span
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      backgroundColor: "#04a189",
                      display: "inline-block",
                    }}
                  ></span>
                  <span className="fw-semibold" style={{ fontSize: "13px" }}>
                    Parsed line items
                  </span>
                </div>
                {!isAllocated && (
                  <button
                    type="button"
                    className="re-btn-sync"
                    style={{
                      fontSize: "12px",
                      height: "28px",
                      padding: "0 10px",
                      gap: "4px",
                    }}
                    onClick={handleAddLine}
                  >
                    + Add line
                  </button>
                )}
              </div>

              {/* Parsed Line Item Dashed Boxes */}
              <div className="mb-3">
                {lines.length === 0 ? (
                  <div className={`p-3 text-center border rounded text-muted small ${isDarkMode ? "bg-dark border-secondary" : "bg-light"}`}>
                    No line items parsed for this invoice yet.
                  </div>
                ) : (
                  lines.map((box, idx) => {
                    const billedVal = Number(box.earning ?? box.billed_amount ?? 0);
                    const billedFmt = billedVal.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    });

                    return (
                      <div
                        key={box.line_item_id || idx}
                        className="p-2 mb-2 rounded"
                        style={{
                          backgroundColor: isDarkMode ? "rgba(13, 148, 136, 0.15)" : "#f0fdfa",
                          border: isDarkMode ? "1px dashed rgba(13, 148, 136, 0.4)" : "1px dashed #0d9488",
                          fontSize: "13px",
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                          color: isDarkMode ? "#5eead4" : "#065f46",
                          whiteSpace: "pre-line",
                          lineHeight: "1.5",
                        }}
                      >
                        <div className="fw-bold" style={{ fontSize: "14px" }}>
                          Line {idx + 1} · {box.line_item_name || "Contract staffing services"} · ₹{billedFmt}
                        </div>
                        <div style={{ fontSize: "13px" }}>
                          {formatBoxSecondLine(box)}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}

          {/* Parsed Line Items Table (MUI DataGrid identical to Invoice Queue table) */}
          <Box
            sx={{
              width: "100%",
              marginBottom: "16px",
              backgroundColor: isDarkMode ? "transparent" : "#fff",
              border: isDarkMode ? "1px solid #444" : "1px solid #e0e0e0",
              borderRadius: "6px",
              overflow: "hidden",
            }}
          >
            <DataGrid
              rows={gridRows}
              columns={resourceColumns}
              autoHeight
              loading={isLoadingResources}
              disableColumnMenu
              hideFooterPagination
              disableRowSelectionOnClick
              getRowId={(row) => row.id}
              rowHeight={52}
              slots={{
                footer: () => {
                  if (lines.length === 0) return null;
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        borderTop: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
                        backgroundColor: isDarkMode ? "#1e1e1e" : "#f8fafc",
                        minHeight: "48px",
                        px: 0,
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 240,
                          pl: "16px",
                          fontWeight: 700,
                          fontSize: "13px",
                          color: isDarkMode ? "#f2f1f1" : "#1e293b",
                        }}
                      >
                        Total
                      </Box>
                      <Box sx={{ width: 155, minWidth: 155, maxWidth: 155, flexShrink: 0 }} />
                      <Box
                        sx={{
                          width: 85,
                          minWidth: 85,
                          maxWidth: 85,
                          flexShrink: 0,
                          textAlign: "right",
                          pr: "10px",
                          fontWeight: 700,
                          fontSize: "13px",
                          color: isDarkMode ? "#f2f1f1" : "#1e293b",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {totals.totalHrs}
                      </Box>
                      <Box
                        sx={{
                          width: 140,
                          minWidth: 140,
                          maxWidth: 140,
                          flexShrink: 0,
                          textAlign: "right",
                          pr: "10px",
                          fontWeight: 700,
                          fontSize: "13px",
                          color: isDarkMode ? "#f2f1f1" : "#1e293b",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {totals.totalBilled.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Box>
                      <Box
                        sx={{
                          width: 130,
                          minWidth: 130,
                          maxWidth: 130,
                          flexShrink: 0,
                          textAlign: "right",
                          pr: "10px",
                          fontWeight: 700,
                          fontSize: "13px",
                          color: isDarkMode ? "#f2f1f1" : "#1e293b",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {totals.hasUnassignedBillable ? (
                          <span style={{ color: "#94a3b8", fontWeight: 500 }}>—</span>
                        ) : (
                          totals.totalCost.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        )}
                      </Box>
                      <Box
                        sx={{
                          width: 130,
                          minWidth: 130,
                          maxWidth: 130,
                          flexShrink: 0,
                          textAlign: "right",
                          pr: "10px",
                          fontWeight: 700,
                          fontSize: "13px",
                          color: totals.hasUnassignedBillable
                            ? "#94a3b8"
                            : totals.totalMargin < 0
                              ? isDarkMode ? "#f87171" : "#dc2626"
                              : isDarkMode ? "#f2f1f1" : "#1e293b",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {totals.hasUnassignedBillable ? (
                          <span style={{ color: "#94a3b8", fontWeight: 500 }}>—</span>
                        ) : (
                          totals.totalMargin.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        )}
                      </Box>
                      <Box
                        sx={{
                          width: 90,
                          minWidth: 90,
                          maxWidth: 90,
                          flexShrink: 0,
                          textAlign: "center",
                          fontWeight: 700,
                          fontSize: "13px",
                          color: totals.hasUnassignedBillable
                            ? "#94a3b8"
                            : totals.totalMargin < 0
                              ? isDarkMode ? "#f87171" : "#dc2626"
                              : isDarkMode ? "#4ade80" : "#16a34a",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {totals.ratio}
                      </Box>
                      {!isAllocated && (
                        <Box sx={{ width: 50, minWidth: 50, maxWidth: 50, flexShrink: 0 }} />
                      )}
                    </Box>
                  );
                },
                noRowsOverlay: CustomNoRowsOverlay,
              }}
              sx={{
                border: "none !important",
                borderRadius: 0,
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
                  borderBottom: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: isDarkMode ? "#f2f1f1" : "inherit",
                },
                "& .MuiDataGrid-cell": {
                  fontSize: "13px",
                  borderBottom: isDarkMode ? "1px solid #2a2a2a" : "1px solid #f0f0f0",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                  outline: "none !important",
                },
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                  outline: "none !important",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: isDarkMode ? "#2a2a2a !important" : "#fafafa",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
                  minHeight: "48px",
                  backgroundColor: isDarkMode ? "#1e1e1e" : "transparent",
                },
              }}
            />
          </Box>

          {/* Single Dynamic Information Banner */}
          {hasInvoiceAmt && !isAllocated && lines.length > 0 && (
            <Box
              sx={{
                mt: 1.5,
                mb: 2.5,
                p: "12px 16px",
                borderRadius: "8px",
                fontSize: "13px",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                backgroundColor: isAmountMatching
                  ? isDarkMode ? "rgba(16, 185, 129, 0.12)" : "#ecfdf5"
                  : isAmountExceeded
                    ? isDarkMode ? "rgba(239, 68, 68, 0.12)" : "#fef2f2"
                    : isDarkMode ? "rgba(245, 158, 11, 0.12)" : "#fffbeb",
                border: isAmountMatching
                  ? isDarkMode ? "1px solid rgba(16, 185, 129, 0.3)" : "1px solid #a7f3d0"
                  : isAmountExceeded
                    ? isDarkMode ? "1px solid rgba(239, 68, 68, 0.35)" : "1px solid #fecaca"
                    : isDarkMode ? "1px solid rgba(245, 158, 11, 0.35)" : "1px solid #fde68a",
                color: isAmountMatching
                  ? isDarkMode ? "#6ee7b7" : "#065f46"
                  : isAmountExceeded
                    ? isDarkMode ? "#fca5a5" : "#991b1b"
                    : isDarkMode ? "#fcd34d" : "#92400e",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <span style={{ fontSize: "18px", lineHeight: 1 }}>
                  {isAmountMatching ? "✓" : isAmountExceeded ? "⚠️" : "ℹ️"}
                </span>
                <div>
                  {isAmountMatching ? (
                    <div>
                      <span className="fw-semibold">Ready to allocate:</span> Total billed ₹
                      {totals.totalBilled.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
                      matches the invoice amount (₹{invTotalAmt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}). {isAllocated ? "Status is Allocated." : "Ready to allocate."}
                    </div>
                  ) : isAmountExceeded ? (
                    <div>
                      <span className="fw-semibold">Amount Exceed:</span> Total billed (₹
                      {totals.totalBilled.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                      exceeds invoice received amount (₹
                      {invTotalAmt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) by{" "}
                      <strong>₹{(totals.totalBilled - invTotalAmt).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>.
                      Resource amounts cannot exceed the invoice received amount. Please adjust the resource amounts.
                    </div>
                  ) : (
                    <div>
                      <span className="fw-semibold">Amount Remaining:</span> Total billed (₹
                      {totals.totalBilled.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                      is less than invoice received amount (₹
                      {invTotalAmt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) by{" "}
                      <strong>₹{(invTotalAmt - totals.totalBilled).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>.
                      Please allocate the remaining amount across resources before confirming.
                    </div>
                  )}
                </div>
              </Box>
            </Box>
          )}

        </>
      )}
    </>
  );

  const renderFooter = () => {
    if (!hasProjectSelected || !selectedBudgetInfo.isAssigned) {
      return null;
    }

    return (
      <>
        <button
          type="button"
          className="re-btn-cancel"
          onClick={handleClose}
        >
          {isAllocated || isMapped ? "Close" : "Cancel"}
        </button>

        <div className="d-flex align-items-center gap-2">
          {activeModel === "Fixed" ? (
            !isAllocated && (
              <>
                {isMapped && onOpenFinalSplit && (
                  <button
                    type="button"
                    className="re-btn-sync"
                    onClick={() =>
                      onOpenFinalSplit({
                        project_id: matchedProjectObj?.project_id || selectedProjectId,
                        project: matchedProjectObj?.title || invoice?.project,
                      })
                    }
                  >
                    Allocate — Project Complete
                  </button>
                )}
                {!isMapped && !isManualInvoice && (
                  <button
                    type="button"
                    className="re-btn-manual"
                    disabled={!hasProjectSelected || !selectedBudgetInfo.isAssigned || isMappingFixed}
                    onClick={handleSaveFixed}
                  >
                    {isMappingFixed ? "Saving..." : "Save Mapping"}
                  </button>
                )}
              </>
            )
          ) : (
            <>
              {!isManualInvoice && (
                <button
                  type="button"
                  className="re-btn-sync"
                  disabled={!hasProjectSelected || !selectedBudgetInfo.isAssigned || lines.length === 0}
                  onClick={() => onOpenReviewMatches?.({ lines, projectUsers, availableUsers: allAvailableUsers })}
                >
                  Review matches
                </button>
              )}
              {!isAllocated && (
                <button
                  type="button"
                  className="re-btn-manual"
                  disabled={
                    !hasProjectSelected ||
                    !selectedBudgetInfo.isAssigned ||
                    lines.length === 0 ||
                    (hasInvoiceAmt && !isAmountMatching) ||
                    isAllocating ||
                    isAllocatingContract ||
                    isParsing ||
                    isFetchingProjectResources
                  }
                  onClick={handleConfirm}
                  style={
                    hasInvoiceAmt && !isAmountMatching
                      ? { opacity: 0.6, cursor: "not-allowed" }
                      : undefined
                  }
                  title={
                    hasInvoiceAmt && !isAmountMatching
                      ? `Total billed (₹${totals.totalBilled.toLocaleString("en-IN")}) must match invoice total (₹${invTotalAmt.toLocaleString("en-IN")})`
                      : undefined
                  }
                >
                  {(isAllocating || isAllocatingContract) && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  )}
                  Confirm & Allocate
                </button>
              )}
            </>
          )}
        </div>
      </>
    );
  };

  if (isInline) {
    return (
      <div
        className="card shadow-sm mb-3"
        style={{
          borderRadius: "8px",
          backgroundColor: isDarkMode ? "#18181b" : "#ffffff",
          borderColor: isDarkMode ? "#27272a" : "#e5e7eb",
          color: isDarkMode ? "#f1f5f9" : "inherit",
        }}
      >
        <div
          className="card-header py-3 px-4 d-flex align-items-center"
          style={{
            backgroundColor: isDarkMode ? "#18181b" : "#ffffff",
            borderBottom: isDarkMode ? "1px solid #27272a" : "1px solid #e5e7eb",
            color: isDarkMode ? "#f1f5f9" : "inherit",
          }}
        >
          {renderHeader()}
        </div>
        <div
          className="card-body p-4"
          style={{
            backgroundColor: isDarkMode ? "#18181b" : "transparent",
            color: isDarkMode ? "#f1f5f9" : "inherit",
          }}
        >
          {renderBody()}
        </div>
        {hasProjectSelected && selectedBudgetInfo.isAssigned && (
          <div
            className="card-footer py-3 px-4 d-flex align-items-center justify-content-between"
            style={{
              backgroundColor: isDarkMode ? "#18181b" : "#f8fafc",
              borderTop: isDarkMode ? "1px solid #27272a" : "1px solid #e5e7eb",
              color: isDarkMode ? "#f1f5f9" : "inherit",
            }}
          >
            {renderFooter()}
          </div>
        )}
      </div>
    );
  }

  return (
    <Modal show={show} onHide={handleClose} centered size="xl">
      <Modal.Header closeButton>
        {renderHeader()}
      </Modal.Header>
      <Modal.Body className="p-4">
        {renderBody()}
      </Modal.Body>
      {hasProjectSelected && selectedBudgetInfo.isAssigned && (
        <Modal.Footer className="bg-light d-flex align-items-center justify-content-between">
          {renderFooter()}
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default ContractMappingModal;
